/**
 * WorkflowParameterContent
 *
 * Body of the workflow parameter sidebar: the document-value preview, the
 * parameter form, the email compose panel and its PDF/XLSX attachments.
 * `WorkflowParameterSidebar` is the shell that owns the Drawer.
 *
 * Split out so `destroyOnHidden` reaches the hooks. The Drawer already carried
 * that prop, but every hook here sat above it, so the four call sites gated the
 * whole component on `{workflow && ...}` to keep them from running while closed
 * — the shape that destroys antd's exit animation. Mounted here, the hooks come
 * and go with the sidebar and the shell can stay mounted (SYD-2264).
 *
 * Mount therefore means open. There is no `open` prop, no `afterOpenChange`
 * re-seeding, and the source form version is resolved here rather than threaded
 * in as props, so no caller subscribes to it on behalf of a closed sidebar.
 *
 * When `splitScope.parameterId` names the send action's multi-select targets
 * parameter it also fans the documents out, rendering one PDF/XLSX set per target
 * (SYD-2302). That loop is strictly sequential: `generatePdfAttachment` owns one
 * portal and is not reentrant.
 *
 * Each target's scope is built by `createSplitQueryFromTargetSource` from the
 * workflow's own `targetSource`, never matched against the form type's split
 * criteria. Requiring a criterion to name the same table and column made every
 * target unpreviewable and every run refused on CLW, whose Quote splits on
 * `parentCompany` while the workflow targets `Manufacturers` (SYD-2412). Every
 * selected target is therefore sendable and previewable; what the lookup was
 * accidentally protecting — a table with no such column, which
 * `resolveSplitForTable` would leave unfiltered — is now `useSplitColumnWarnings`,
 * a pre-flight that holds Run and Preview PDF disabled and names the table.
 *
 * That pre-flight covers both export views a send renders at, never one. The PDF
 * portal renders at `pdfRenderVisibility` and the XLSX exports at
 * `pdfVisibilityMode`, and the two differ whenever Include PDF is off while a
 * `savePdfToForm` action still builds one at its own view. `exportVisibilityModes`
 * is that union, and `useVisibleTableIds` turns it into the tables to check.
 * Preview PDF opens at `pdfRenderVisibility` for the same reason — it shows the
 * document the run attaches, not the XLSX's view — and it renders whenever
 * `needsPdf` is true: beside the Export view Select while Include PDF is on, the
 * one state in which those two views are the same view, and in a row of its own
 * under the parameter form whenever they can differ (SYD-2392).
 *
 * A target whose recipient resolution is not ready is selectable and previewable
 * all the same. What decides whether it is sent to is its effective `to` list,
 * never its resolver status — the recipient panel lets the operator type an
 * address for such a target, and that address lives in this run's overrides
 * only, never written back to the entity's recipients tree (SYD-2414).
 *
 * Preview PDF renders one of those same declared per-target documents rather than
 * the on-screen split, so what the operator reads is what the email carries. The
 * dropdown that appears past one selected target navigates between the documents
 * the declaration already produces; it is not a choice of how to split.
 *
 * A fan-out recipient whose document would carry no rows of theirs is refused
 * rather than sent (SYD-2410). `useRecipientRowCounts` counts, live, the rows
 * that belong to each selected target and survive the operator's ticks and the
 * grid's column filters — both narrow the exported document — and
 * `emptyTargetIds` is the one set the Run gate, the Preview PDF menu and the
 * exclusion block in `handleOk` all read. That set is empty while the count is
 * in flight, while a rows listener has failed and while a rendered table has no
 * split column, so the refusal never fires on missing evidence — those states
 * hold Run with a reason of their own instead.
 * A shared document has no per-target scope and is never refused this way.
 * Both exclusions — this one and the address one — are folded back into the run's
 * summary before it reaches the banner: they happen in the browser, so the server
 * counts only the recipients it was handed and reports a `skipped` that is short.
 *
 * That count is only sound while the scope it counted cannot move, so
 * `handleRunClick` holds `formStore.state.isDispatchingWorkflowRun` for the whole
 * run: the two builders read the live ticks and column filters once per document,
 * tens of seconds later, and a tick landing mid-run would leave every recipient
 * built after it with rows-of-theirs ∩ that tick — nothing. The grid's selection,
 * scope bar, column filters and pagination render disabled for the duration.
 *
 * The run is a three-phase state owned here — `idle` → `running` → `done` — not by
 * `useWorkflowRunner`: the pre-flight it has to cover (validation, file sharing,
 * one PDF/XLSX build per recipient) all happens in `handleOk`, tens of seconds
 * before the runner is ever entered. A caller's `isRunning` therefore left the Run
 * button live through the whole build and a second click sent a second quote
 * (SYD-2442). While `running` every compose control is disabled — editing one after
 * the payload is built cannot reach the run.
 *
 * Only a successful outcome reaches `done`, and it latches there until the operator
 * changes a parameter. A failure goes back to `idle` carrying its banner, because
 * `done` has no exit at all on a workflow with no `userParameters` and no send
 * action: the Form that fires `onValuesChange` is not rendered, and nothing on
 * screen can move the compose signature. A 503 from Signify would otherwise read
 * as Done and force a close-and-reopen to retry.
 *
 * Editing a compose field and editing it back resurrects a `done` run, since the
 * signature is a value hash; the same round trip on a Form field does not, because
 * `onValuesChange` fires on the way out. Both land on Run being pressable, so the
 * asymmetry costs nothing.
 *
 * Stop is that three-phase state's counterpart, and it is driven from outside it:
 * `stopPhase` comes from the host's `useWorkflowRunner`, which alone knows when the
 * `executeWorkflow` callable is in flight. The button is therefore dead through the
 * whole browser-side pre-flight — nothing external has happened yet — live only while
 * the server holds the run, and back to dead once the response is in. A stop lands on
 * the banner like a failure and latches nothing; a success that arrives while a stop
 * the server accepted was pending latches Done and says the ask came too late. A stop
 * the server refused never reached the run, so it qualifies nothing (SYD-2443).
 */

import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AutoComplete, Button, Checkbox, DatePicker, Divider, Dropdown, Form, Input, InputNumber, Select, Space, Tooltip, Typography, theme } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useSnapshot } from 'valtio';
import { createCollectionStore } from '@/store';
import type { User as SystemUser } from '#types/System.types';

const usersStore = createCollectionStore<SystemUser>('users', {
  queryOptions: { extraConstraints: [{ type: 'limit', n: 5000 }] },
});
import type { VisibilityTypeId } from '#types/System.types';
import type {
  FormVersion,
  Workflow,
  WorkflowAction,
  WorkflowCallSignifyApiPostAction,
  WorkflowColumnSource,
  WorkflowRecipientEmailGroups,
  WorkflowSendOverrides,
  WorkflowUserParameter,
  WorkflowEmailAttachment,
  WorkflowRunAttachments,
  WorkflowValueSource,
  WorkflowSavePdfToFormAction,
  ResolveWorkflowRecipientPreviewResponse,
} from '#types';
import { EMAIL_MAX_LENGTH, EMAIL_REGEX, replyToChoicesFor } from '#types';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import { resolveTableColumnValues, resolveWorkflowRecipientPreview, resolveWorkflowTargets } from '@/workflows/services/workflowFunctions';
import type {
  RelationshipTreeNode,
  RelationshipTreeValue,
} from '#types';
import { MessageContext } from '@/providers/MessageContext';
import { UserContext } from '@/providers/UserContext';
import { formActions, formStore, useFormStore } from '@/store';
import { useWorkflowSourceFormVersion } from './useWorkflowSourceFormVersion';
import { useSplitColumnWarnings } from './useSplitColumnWarnings';
import { useRecipientRowCounts } from './useRecipientRowCounts';
import type { WorkflowRunOutcome } from './workflowRunOutcome.types';
import type { WorkflowRunStopPhase } from './workflowRunStopPhase.types';
import { workflowRunErrorOutcome } from './workflowRunErrorOutcome';
import formatStopRefusalMessage from './formatStopRefusalMessage';
import { workflowRunStageLabel } from './workflowRunStageLabel';
import { workflowActionTypeLabel } from '@/workflows/workflowActionTypeLabel';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { createSplitQueryFromTargetSource, useFormSplit } from '@/use/form/useFormSplit';
import { useVisibleTableIds } from '@/use/form/useVisibleTableIds';
import { useOrganizationParameters } from '@/use/useOrganizationParameters';
import { useCurrentArtifact } from '@/use/useCurrentArtifact';
import { usePdfAttachment } from '@/components/modals/FormEmailModal/use/usePdfAttachment';
import { useXlsxAttachment } from '@/components/modals/FormEmailModal/use/useXlsxAttachment';
import FormExportModal from '@/components/pdfExport/components/FormExportModal';
import FileLinksSelector, { ShareableFile } from '@/components/selectors/FileLinksSelector';
import { useAttachments } from '@/use/attachments/useAttachments';
import { useFileSharing } from '@/components/modals/FormEmailModal/use/useFileSharing';
import { useEmailTemplateContext } from '@/components/modals/FormEmailModal/useEmailTemplateContext';
import { useEmailTemplateResolution } from '@/components/modals/FormEmailModal/useEmailTemplateResolution';
import { createFileLinksSection } from '@/components/modals/FormEmailModal/create/createFileLinksSection';
import AddressUI from '@/components/rendering/fields/AddressUI/AddressUI';
import { ShippingTrackerCompactUI } from '@/components/rendering/fields/ShippingTrackerUI';
import SignifyDistributorSelector from '@/api/signify/SignifyProductButton/SignifyProductModal/components/SignifyDistributorSelector';
import { useSignifyDistributors } from '@/api/signify/api';
import { DEFAULT_SIGNIFY_CUSTOMER_ID } from '#types/SignifyApi.types';
import { defaultSystemFromEmail, MAX_LENGTHS, type ReplyToChoice } from '#types/System.Parameters.types';
import { fetchRecord } from '@/store/entityRecordsRegistry';
import { dateOnlyTimestamp } from '@/dates';

type ResolvedOption = { value: string; label: string };

const { Text } = Typography;
const { TextArea } = Input;

async function resolveDefaultValueSource(
  source: WorkflowValueSource | undefined,
  entityReferences: FormVersion['entityReferences'] | undefined,
): Promise<string | null> {
  if (!source) return null;
  if (source.sourceType === 'entityReferenceValue') {
    const ref = entityReferences?.[source.entityOrGroupId];
    if (!ref) return null;
    const value = source.valueType === 'entityRecordName'
      ? ref.entityRecordName
      : ref.entityRecordId;
    return typeof value === 'string' && value.length > 0 ? value : null;
  }
  if (source.sourceType === 'entityRecordField') {
    const recordId = await resolveDefaultValueSource(source.recordIdSource, entityReferences);
    if (!recordId) return null;
    const record = await fetchRecord(source.entityOrGroupId, recordId);
    const value = record?.fields?.[source.fieldId]?.value;
    return typeof value === 'string' && value.length > 0 ? value : null;
  }
  return null;
}

const DistributorSelectField: FC<{
  parameterId: string;
  workflowDocId?: string;
  defaultValueSource?: WorkflowValueSource;
  entityReferences?: FormVersion['entityReferences'];
  onChange: (value: string) => void;
}> = ({ defaultValueSource, entityReferences, onChange }) => {
  const {
    distributors: filteredDistributors,
    loading: loadingDistributors,
    searchTerm: distributorSearchTerm,
    setSearchTerm: setDistributorSearchTerm,
    refreshDistributors,
    loadDistributorsIfNeeded,
    totalCount: totalDistributors,
    filteredCount: filteredDistributorsCount,
  } = useSignifyDistributors();
  const [searchValue, setSearchValue] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [autoSelected, setAutoSelected] = useState(false);
  const [resolvedDefaultId, setResolvedDefaultId] = useState<string | null | undefined>(
    defaultValueSource ? undefined : null,
  );
  const resolverStartedRef = useRef(false);

  useEffect(() => {
    loadDistributorsIfNeeded();
  }, [loadDistributorsIfNeeded]);

  if (defaultValueSource && !resolverStartedRef.current) {
    resolverStartedRef.current = true;
    resolveDefaultValueSource(defaultValueSource, entityReferences)
      .then(setResolvedDefaultId)
      .catch(() => setResolvedDefaultId(null));
  }

  if (!autoSelected && !selectedId && filteredDistributors.length > 0 && resolvedDefaultId !== undefined) {
    const resolvedMatch = resolvedDefaultId
      ? filteredDistributors.find((d) => d.uid === resolvedDefaultId)
      : undefined;
    const defaultDistributor = resolvedMatch
      ?? filteredDistributors.find((d) => d.uid === DEFAULT_SIGNIFY_CUSTOMER_ID)
      ?? filteredDistributors[0];
    if (defaultDistributor) {
      setAutoSelected(true);
      setSelectedId(defaultDistributor.uid);
      setSearchValue(defaultDistributor.name);
      queueMicrotask(() => onChange(defaultDistributor.uid));
    }
  }

  return (
    <SignifyDistributorSelector
      selectedDistributor={selectedId}
      distributorSearchValue={searchValue}
      loading={false}
      loadingDistributors={loadingDistributors}
      filteredDistributors={filteredDistributors}
      filteredDistributorsCount={filteredDistributorsCount}
      totalDistributors={totalDistributors}
      distributorSearchTerm={distributorSearchTerm}
      onDistributorSearch={(value) => {
        setSearchValue(value);
        setDistributorSearchTerm(value);
      }}
      onDistributorSelect={(value) => {
        const d = filteredDistributors.find((x) => x.uid === value);
        setSelectedId(value);
        setSearchValue(d?.name ?? value);
        onChange(value);
      }}
      onDistributorClear={() => {
        setSelectedId('');
        setSearchValue('');
        onChange('');
      }}
      onRefreshDistributors={refreshDistributors}
    />
  );
};

const recipientRoleOrder: Array<keyof WorkflowRecipientEmailGroups> = ['to', 'cc', 'bcc'];
const recipientRoleLabels: Record<keyof WorkflowRecipientEmailGroups, string> = {
  to: 'To',
  cc: 'Cc',
  bcc: 'Bcc',
};

/**
 * Same shape as `normalizeEmail` in `resolveWorkflowRecipients`, over the same shared
 * `EMAIL_REGEX` / `EMAIL_MAX_LENGTH`, so an address the operator types into the send sidebar
 * cannot pass here and then be dropped server-side.
 */
function normalizeRecipientEmail(raw: string): string | undefined {
  const candidate = raw.trim().toLowerCase();
  if (candidate.length === 0) return undefined;
  if (candidate.length > EMAIL_MAX_LENGTH) return undefined;
  if (!EMAIL_REGEX.test(candidate)) return undefined;
  return candidate;
}

const ATTACHMENT_SELECTION_SEPARATOR = ':';

function matchesAttachmentMimeType(params: {
  fileName: string;
  mimeType: string;
  allowedMimeTypes?: string[];
}): boolean {
  const { fileName, mimeType, allowedMimeTypes } = params;
  if (!allowedMimeTypes || allowedMimeTypes.length === 0) return true;
  if (allowedMimeTypes.includes(mimeType)) return true;
  return allowedMimeTypes.includes('application/pdf') && fileName.toLowerCase().endsWith('.pdf');
}

/**
 * One option per attachment version, labelled by the attachment's current
 * name: the artifact's authoritative `fileName` is what a rename writes, so it
 * comes first. `versionFileName` is stamped once at commit and still reads as
 * the pre-rename name, which left two renamed look-alike exports
 * indistinguishable in the drop-down (SYD-2979, SYD-2457); it survives only as
 * the fallback for an entry carrying no `fileName` of its own.
 *
 * The mime filter keeps reading the committed name, because a rename is free
 * text: an operator who drops the `.pdf` off a PDF whose version carries no
 * usable mime type would otherwise drop it out of a PDF-only parameter.
 */
function buildAttachmentSelectOptions(
  attachments: FormVersion['attachments'] | undefined,
  allowedMimeTypes?: string[],
): Array<{ value: string; label: string }> {
  if (!attachments) return [];

  return Object.entries(attachments)
    .flatMap(([fileId, fileData]) =>
      Object.entries(fileData.versions ?? {}).map(([versionId, version]) => {
        const committedFileName = version.versionFileName || '';
        return {
          fileId,
          versionId,
          fileName: fileData.fileName?.trim() || committedFileName || 'Unnamed file',
          committedFileName,
          versionNumber: version.versionNumber || 1,
          mimeType: version.type || '',
        };
      })
    )
    .filter((option) => matchesAttachmentMimeType({
      fileName: option.committedFileName || option.fileName,
      mimeType: option.mimeType,
      allowedMimeTypes,
    }))
    .sort((a, b) => {
      const nameCompare = a.fileName.localeCompare(b.fileName);
      if (nameCompare !== 0) return nameCompare;
      return b.versionNumber - a.versionNumber;
    })
    .map((option) => ({
      value: `${option.fileId}${ATTACHMENT_SELECTION_SEPARATOR}${option.versionId}`,
      label: `${option.fileName} (v${option.versionNumber})`,
    }));
}

/**
 * The source table's current selection and split, as the sidebar labels the run and
 * scopes parameter option lookups by. Display and parameter concerns only — the rows
 * a run may touch travel separately as `WorkflowRowScope` on the request (SYD-2322).
 */
type TableViewScope = {
  selectedCount: number;
  totalCount: number;
  selectedRowIds?: string[];
  splitLabel?: string;
  splitFilter?: { columnId: string; value: string };
};

type WorkflowParameterContentProps = {
  workflow: Workflow;
  sourceFormId?: string;
  sourceVersionId?: string;
  sourceParentPath?: string;
  formNumber?: string;
  formTitle?: string;
  initialValues?: Record<string, unknown>;
  autoSelectAll?: boolean;
  tableScope?: TableViewScope;
  onCancel: () => void;
  /**
   * `null` means the caller refused the run, so nothing was dispatched and the Run
   * button stays idle with no banner. A success latches it into its Done state; an
   * error leaves it on Run and shows why.
   */
  onConfirm: (
    params: Record<string, unknown>,
    attachments?: WorkflowRunAttachments,
  ) => Promise<WorkflowRunOutcome | null>;
  /**
   * Asks the run the host currently has in flight to end. Rejects only on a refusal
   * the operator has to see — a run that finished first resolves silently.
   */
  onStop: () => Promise<void>;
  /** From the host's `useWorkflowRunner`; Stop is live only while this is `stoppable`. */
  stopPhase: WorkflowRunStopPhase;
};

type WorkflowRunPhase = 'idle' | 'running' | 'done';

type WorkflowRunState =
  /**
   * `report` is the last dispatch that came back something other than a success, kept
   * on the banner while the button is live again: an error the operator has to fix, or
   * a stop they asked for and may want to dispatch past (SYD-2443).
   */
  | { phase: 'idle'; report?: Extract<WorkflowRunOutcome, { status: 'error' | 'stopped' }> }
  | { phase: 'running' }
  /** `signature` is the compose state as it stood when `onConfirm` resolved. */
  | {
      phase: 'done';
      outcome: Extract<WorkflowRunOutcome, { status: 'success' }>;
      signature: string;
    };

/** Above this the `executeWorkflow` callable's 10 MB request ceiling is at risk. */
const MAX_ATTACHMENT_PAYLOAD_BYTES = 8 * 1024 * 1024;

/**
 * Why Run and Preview PDF are both refused while `useSplitColumnWarnings` is in
 * flight. One string so the banner and the button's tooltip cannot drift.
 */
const SPLIT_COLUMN_CHECK_PENDING = 'Checking that every table carries the split column.';

/**
 * Why Run and Preview PDF are both refused while `useRecipientRowCounts` is in
 * flight. One string so the banner and the button's tooltip cannot drift.
 */
const RECIPIENT_ROWS_CHECK_PENDING = 'Checking which recipients have rows in their documents.';

/**
 * Keys the two exclusion warnings are raised under. Both persist — an operator
 * must not miss who was dropped from a send — so each run destroys the previous
 * run's pair before dispatching. Without that, a stale reason stays on screen
 * beside the current run's and names a cause that no longer applies to it.
 */
const EXCLUDED_NO_EMAIL_MESSAGE_KEY = 'workflow-run-excluded-no-email';
const EXCLUDED_NO_ROWS_MESSAGE_KEY = 'workflow-run-excluded-no-rows';

const fieldName = (param: WorkflowUserParameter): string => param.parameterId;

/** What a `date` parameter's picker displays when it declares no `format`, and the shape
 *  a `filterBy` gate compares that parameter's pick against. */
const DEFAULT_DATE_PARAMETER_FORMAT = 'YYYY-MM-DD';

function isRelationshipTreeNode(value: unknown): value is RelationshipTreeNode {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<RelationshipTreeNode>;
  const treeType = (candidate as Record<string, unknown>).treeType;
  const isValidTreeType =
    treeType === 'contractor' ||
    treeType === 'specifier' ||
    treeType === 'recipient' ||
    treeType === 'other';
  return (
    typeof candidate.key === 'string' &&
    isValidTreeType &&
    typeof candidate.entityOrGroupId === 'string'
  );
}

function isRelationshipTreeValue(value: unknown): value is RelationshipTreeValue {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<RelationshipTreeValue>;
  if (!Array.isArray(candidate.trees)) return false;
  return candidate.trees.every((t) => isRelationshipTreeNode(t));
}

function dedupeStrings(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    if (!item) continue;
    if (seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
}

function collectRelationshipTreeEntityRecordIds(params: {
  value: RelationshipTreeValue;
  entityOrGroupIds?: string[];
}): string[] {
  const { value, entityOrGroupIds } = params;
  const allowed = entityOrGroupIds ? new Set(entityOrGroupIds) : null;

  const out: string[] = [];
  const walk = (nodes: RelationshipTreeNode[]) => {
    for (const node of nodes) {
      if (node.entityRecordId && (!allowed || allowed.has(node.entityOrGroupId))) {
        out.push(node.entityRecordId);
      }
      if (node.children && node.children.length > 0) {
        walk(node.children);
      }
    }
  };

  walk(value.trees ?? []);
  return out;
}

function resolveValueSourcePreview(
  source: WorkflowValueSource,
  fields?: FormVersion['fields'],
  entityReferences?: FormVersion['entityReferences'],
): unknown {
  if (source.sourceType === 'literal') return source.value;
  if (source.sourceType === 'formFieldValue') return fields?.[source.fieldId]?.value;
  if (source.sourceType === 'entityReferenceValue') {
    const ref = entityReferences?.[source.entityOrGroupId];
    return source.valueType === 'entityRecordName' ? ref?.entityRecordName : ref?.entityRecordId;
  }
  if (source.sourceType === 'userParameter') return undefined;
  return undefined;
}

function resolveColumnSourceLabel(source: WorkflowColumnSource): string {
  if (typeof source === 'string') return source || 'Not configured';
  if (source.source === 'entityRecordField') {
    if (!source.fieldId) return 'Not configured';
    return `via ${source.entityId} → ${source.fieldId}`;
  }
  return 'Not configured';
}

function isColumnSourceConfigured(source: WorkflowColumnSource): boolean {
  if (typeof source === 'string') return source.length > 0;
  if (source.source === 'entityRecordField') return source.fieldId.length > 0;
  return false;
}

const REQUIRED_PRODUCT_COLUMNS: Array<keyof WorkflowCallSignifyApiPostAction['productMapping']['columns']> = [
  'code', 'quantity', 'type', 'customerReference',
];

const SELF_RESOLVING_SOURCE_TYPES: Set<string> = new Set([
  'literal', 'userParameter', 'currentUserProperty', 'concatenate', 'formProperty',
]);


function collectConfigWarnings(
  actions: WorkflowAction[],
  fields?: FormVersion['fields'],
  entityReferences?: FormVersion['entityReferences'],
): string[] {
  const warnings: string[] = [];
  for (const action of actions) {
    if (action.actionType !== 'callSignifyApi') continue;
    if (!('productMapping' in action)) continue;
    const postAct = action as WorkflowCallSignifyApiPostAction;
    if (!postAct.productMapping.sourceTableId) {
      warnings.push('Source table is not configured');
    }
    if (!postAct.fieldMappings) continue;
    for (const [key, source] of Object.entries(postAct.fieldMappings) as Array<[string, WorkflowValueSource]>) {
      if (source.sourceType === 'formFieldValue' && !source.fieldId) {
        warnings.push(`Field "${key}" has no form field configured`);
      }
      if (source.sourceType === 'entityReferenceValue' && !source.entityOrGroupId) {
        warnings.push(`Field "${key}" has no entity reference configured`);
      }
      if (!SELF_RESOLVING_SOURCE_TYPES.has(source.sourceType)) {
        const resolved = resolveValueSourcePreview(source, fields, entityReferences);
        if (resolved == null || resolved === '') {
          warnings.push(`Field "${key}" resolved to empty value`);
        }
      }
    }
    for (const col of REQUIRED_PRODUCT_COLUMNS) {
      const source = postAct.productMapping.columns[col];
      if (!source || !isColumnSourceConfigured(source)) {
        warnings.push(`Required product column "${col}" is not configured`);
      }
    }
  }
  if (warnings.length > 0) {
    console.warn(`[Workflow] Config warnings: ${warnings.join(' | ')}`);
  }
  return warnings;
}

const WorkflowParameterContent: FC<WorkflowParameterContentProps> = ({
  workflow,
  sourceFormId,
  sourceVersionId,
  sourceParentPath,
  formNumber,
  formTitle,
  initialValues,
  autoSelectAll,
  tableScope,
  onCancel,
  onConfirm,
  onStop,
  stopPhase,
}) => {
  const { token } = theme.useToken();
  const { messageApi } = useContext(MessageContext);
  const { user } = useContext(UserContext);
  const {
    fields: sourceFormVersionFields,
    entityReferences: sourceFormVersionEntityReferences,
    loading: sourceFormVersionLoading,
  } = useWorkflowSourceFormVersion(sourceParentPath);
  const { formType, formId, versionId } = useFormStore();
  const { fieldTypes, isLoading: fieldTypesLoading } = useContext(FieldTypeContext);
  const splitConfig = formType.data?.splitConfiguration;
  const { splitOptions } = useFormSplit(
    splitConfig,
    fieldTypes,
    formId && versionId ? `forms/${formId}/formVersions/${versionId}` : undefined,
    !!splitConfig?.enabled,
  );
  const currentArtifact = useCurrentArtifact();
  const formEntityData = currentArtifact.kind === 'none' ? null : currentArtifact.data;
  const { data: orgData, getVisibilityTypesForFormType } = useOrganizationParameters();
  const emailSenderPolicy = orgData?.emailSenderPolicy;
  const [emailSenderModeOverride, setEmailSenderModeOverride] = useState<ReplyToChoice | undefined>(undefined);
  const [copySenderOverride, setCopySenderOverride] = useState<boolean | undefined>(undefined);
  const { functionalAccounts, choice: emailSenderMode, copySender, copySenderFixed } = replyToChoicesFor(
    emailSenderPolicy,
    user?.uid,
    emailSenderModeOverride,
    copySenderOverride,
  );
  const senderCopyAddress = copySender && user?.email ? normalizeRecipientEmail(user.email) : undefined;
  const replyToOptions = [
    { label: `System email (${defaultSystemFromEmail})`, value: 'system' },
    { label: `My email (${user?.email})`, value: 'user' },
    ...functionalAccounts.map((account) => ({ label: `${account.label} (${account.email})`, value: account.email })),
  ];

  const [form] = Form.useForm<Record<string, unknown>>();
  const [resolvedOptionsByParamId, setResolvedOptionsByParamId] = useState<Record<string, ResolvedOption[]>>({});
  const [resolvedOptionsLoading, setResolvedOptionsLoading] = useState(false);
  const [resolvedOptionsError, setResolvedOptionsError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const autoSelectPendingRef = useRef(autoSelectAll === true);

  useEffect(() => {
    usersStore.connect();
    return () => usersStore.disconnect();
  }, []);

  const usersSnap = useSnapshot(usersStore.state);
  const userOptions = useMemo(() => {
    const result: { value: string; label: string }[] = [];
    for (const id of usersSnap.docIds) {
      const entry = usersSnap.docs.get(id);
      if (!entry) continue;
      const u = entry.data as SystemUser;
      result.push({
        value: id,
        label: u.name || u.email || id,
      });
    }
    return result;
  }, [usersSnap.docIds, usersSnap.docs]);

  const params = useMemo(() => workflow.userParameters ?? [], [workflow.userParameters]);

  /**
   * The parameters whose declared type is `date`, so `handleOk` can send each as a
   * `Timestamp` at start of the picked day in `defaultTimeZone` (SYD-1785). Gated on
   * the declared type rather than on `dayjs.isDayjs` alone: a parameter that carries a
   * time of day must not be flattened to start of day by the same pass.
   */
  const dateParameterIds = useMemo(
    () => new Set(params.filter((param) => param.type === 'date').map((param) => param.parameterId)),
    [params],
  );

  const triggerTableId = useMemo(() => {
    for (const action of workflow.actions ?? []) {
      if ('productMapping' in action && action.productMapping?.sourceTableId) {
        return action.productMapping.sourceTableId;
      }
      if ('sourceTableType' in action && action.sourceTableType) {
        return action.sourceTableType;
      }
    }
    for (const p of params) {
      if (p.type === 'tableColumnSelect') return p.tableId;
    }
    return undefined;
  }, [workflow.actions, params]);

  const mergedInitialValues = useMemo(() => {
    const defaults: Record<string, unknown> = {};
    for (const param of workflow.userParameters ?? []) {
      if (param.type === 'select' && param.defaultValue !== undefined) {
        defaults[param.parameterId] = param.defaultValue;
      }
    }
    return { ...defaults, ...initialValues };
  }, [workflow.userParameters, initialValues]);

  const sendAction = useMemo(() => {
    return (workflow.actions ?? []).find(
      (action): action is Extract<WorkflowAction, { actionType: 'send' }> => action.actionType === 'send'
    );
  }, [workflow.actions]);
  const savePdfToFormAction = useMemo(() => {
    return (workflow.actions ?? []).find(
      (action): action is WorkflowSavePdfToFormAction => action.actionType === 'savePdfToForm'
    );
  }, [workflow.actions]);
  const recipientResolver = sendAction?.recipientResolver;
  const contactResolver = recipientResolver?.resolverType === 'entityContact' ? recipientResolver : undefined;
  const showRecipientPreview = recipientResolver?.resolverType === 'user'
    || contactResolver?.useRecipientsTree === true
    || contactResolver?.useSourceTreeContacts === true;
  const targetsParameterId = sendAction?.targetsParameterId;
  const targetsParameterLabel = useMemo(
    () => params.find((p) => p.parameterId === targetsParameterId)?.label ?? 'recipients',
    [params, targetsParameterId],
  );

  /**
   * What the operator sees for a target: the label its parameter resolved,
   * falling back to the id. One definition for the preview menu, for the filename
   * suffix each per-target document carries and for every exclusion warning, so a
   * document can never be named differently from the entry that opened it.
   */
  const labelForTarget = useCallback(
    (targetId: string): string =>
      (targetsParameterId ? resolvedOptionsByParamId[targetsParameterId] : undefined)
        ?.find((option) => option.value === targetId)?.label
      ?? targetId,
    [resolvedOptionsByParamId, targetsParameterId],
  );

  /**
   * What the operator calls the tree a `relationshipTree` parameter reads its
   * targets out of: the label this version stores on the field, then the field
   * type's own, then the generic name. A form whose tree field was never filled
   * in carries no field on the version at all, so the field type is the only
   * label such a form can offer.
   */
  const treeFieldLabel = useCallback(
    (fieldId: string): string =>
      sourceFormVersionFields?.[fieldId]?.description?.shortLabel
      ?? fieldTypes[fieldId]?.description?.shortLabel
      ?? 'Relationship Tree',
    [sourceFormVersionFields, fieldTypes],
  );

  const formValues = Form.useWatch([], form);
  const selectedTargetIds = useMemo(() => {
    if (!targetsParameterId) return [];
    const raw = (formValues as Record<string, unknown> | undefined)?.[targetsParameterId];
    if (typeof raw === 'string') {
      return raw.length > 0 ? [raw] : [];
    }
    if (Array.isArray(raw)) {
      return raw.filter((value): value is string => typeof value === 'string' && value.length > 0);
    }
    return [];
  }, [formValues, targetsParameterId]);
  /**
   * The string each gated parameter's `filterBy` matches a column value against. A `date`
   * gate's field holds the Dayjs its picker natively returns since SYD-1785, so it is
   * formatted here in the parameter's own display format — the exact string the deleted
   * `normalize` used to put in the form store, so a gate keeps comparing what it did.
   */
  const gatingValueByParamId = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    for (const p of params) {
      const filterBy = (p.type === 'tableColumnSelect' || p.type === 'resolvedSelect') ? p.filterBy : undefined;
      if (!filterBy) continue;
      const gatingParam = params.find((q) => q.parameterId === filterBy.parameterId);
      const raw = (formValues as Record<string, unknown> | undefined)?.[filterBy.parameterId];
      const value = gatingParam?.type === 'date' && dayjs.isDayjs(raw)
        ? raw.format(gatingParam.format ?? DEFAULT_DATE_PARAMETER_FORMAT)
        : raw;
      map[p.parameterId] = typeof value === 'string' && value.length > 0 ? value : undefined;
    }
    return map;
  }, [params, formValues]);
  const gatingParamLabelByParamId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of params) {
      const filterBy = (p.type === 'tableColumnSelect' || p.type === 'resolvedSelect') ? p.filterBy : undefined;
      if (!filterBy) continue;
      const gatingParam = params.find((q) => q.parameterId === filterBy.parameterId);
      if (gatingParam) map[p.parameterId] = gatingParam.label ?? filterBy.parameterId;
    }
    return map;
  }, [params]);
  const resolvedFieldMappingPreview = useMemo(() => {
    const entries: Array<{ label: string; value: string }> = [];
    for (const action of workflow.actions ?? []) {
      if (action.actionType !== 'callSignifyApi') continue;
      if (!('fieldMappings' in action)) continue;
      const postAct = action as WorkflowCallSignifyApiPostAction;
      for (const [key, source] of Object.entries(postAct.fieldMappings) as Array<[string, WorkflowValueSource]>) {
        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
        const resolved = resolveValueSourcePreview(source, sourceFormVersionFields, sourceFormVersionEntityReferences);
        if (resolved != null) entries.push({ label, value: String(resolved) });
      }
    }
    return entries;
  }, [workflow.actions, sourceFormVersionFields, sourceFormVersionEntityReferences]);

  const productColumnPreview = useMemo(() => {
    const entries: Array<{ column: string; label: string; configured: boolean; required: boolean }> = [];
    for (const action of workflow.actions ?? []) {
      if (action.actionType !== 'callSignifyApi') continue;
      if (!('productMapping' in action)) continue;
      const postAct = action as WorkflowCallSignifyApiPostAction;
      const columns = postAct.productMapping.columns;
      for (const [col, source] of Object.entries(columns) as Array<[string, WorkflowColumnSource]>) {
        const required = (REQUIRED_PRODUCT_COLUMNS as string[]).includes(col);
        const configured = isColumnSourceConfigured(source);
        if (!required && !configured) continue;
        entries.push({ column: col, label: resolveColumnSourceLabel(source), configured, required });
      }
    }
    return entries;
  }, [workflow.actions]);

  const tableScopeLabel = useMemo(() => {
    if (!tableScope) return null;
    const hasApiAction = (workflow.actions ?? []).some((a) => a.actionType === 'callSignifyApi');
    if (!hasApiAction) return null;
    if (tableScope.selectedCount === 0) {
      if (tableScope.splitLabel) return `Rows split by ${tableScope.splitLabel}`;
      return `All ${tableScope.totalCount} rows`;
    }
    return `${tableScope.selectedCount} of ${tableScope.totalCount} rows`;
  }, [workflow.actions, tableScope]);

  /**
   * The send fans out into one document set per target when `splitScope` names
   * the send action's own targets parameter and that parameter is a multi-select
   * (SYD-2302). `splitScope.parameterId` already means "scope the form by this
   * parameter's value"; at `mode: 'multi'` that is one scope per value, so no
   * second config field states the same fact.
   *
   * A `tableColumn` target source is required, so a saved workflow whose targets
   * parameter reads a `relationshipTree` or a `userField` is not per-target at all
   * — it keeps the single shared document set. Without a table and a column there
   * is nothing to scope each document by.
   *
   * `tableId` and `columnId` are the whole per-target scope: they become the
   * `splitRef` of the `SplitQuery` each target's document is built at. Nothing
   * here consults the form type's `splitConfiguration` — Quote fans out on
   * `Manufacturers` while its only split criterion names `parentCompany`, and
   * requiring the two to agree refused every run (SYD-2412).
   */
  const perTargetSplit = useMemo(() => {
    const parameterId = workflow.splitScope?.parameterId;
    if (!parameterId || !targetsParameterId || parameterId !== targetsParameterId) return undefined;
    const param = params.find((p) => p.parameterId === parameterId);
    if (param?.type !== 'resolvedSelect' || param.mode !== 'multi') return undefined;
    if (param.targetSource.sourceType !== 'tableColumn') return undefined;
    return {
      parameterId,
      label: param.label,
      tableId: param.targetSource.tableId,
      columnId: param.targetSource.columnId,
    };
  }, [workflow.splitScope, targetsParameterId, params]);

  /**
   * The split parameter's Select is filled from a different list than the one
   * `splitOptions` holds — server-resolved column values, author-defined options,
   * or free text under `allowFreeInput` — while `splitOptions` comes from AR-table
   * row listeners that `destroyOnHidden` leaves cold on every open. Picking a
   * value before those land finds no match, so `splitQuery` keeps its previous
   * pin and the run would execute against a scope the form is not showing.
   *
   * Derived at read time from the already-watched form values, so it self-clears
   * the moment the rows arrive and a match appears. Deliberately not folded into
   * `configWarnings`: that memo's deps carry neither `splitOptions` nor
   * `formValues`, so it would never recompute.
   *
   * A single-value scope only. A multi-select value is an array, and a fan-out
   * run never pins the on-screen split: each target's scope is synthesized from
   * the workflow's own target source, so there is no option for it to miss
   * (SYD-2412). What protects a fan-out run instead is `useSplitColumnWarnings`,
   * which refuses a send whose tables cannot carry the split at all.
   */
  const splitScopeValue = workflow.splitScope?.parameterId
    ? (formValues as Record<string, unknown> | undefined)?.[workflow.splitScope.parameterId]
    : undefined;
  const splitScopeUnmatched = typeof splitScopeValue === 'string'
    && splitScopeValue.length > 0
    && !splitOptions.some((opt) => opt.actualValue === splitScopeValue);

  const defaultIncludePdf = sendAction?.email.includePdf === true;
  const defaultIncludeXlsx = sendAction?.email.includeXlsx === true;
  const [includePdfOverride, setIncludePdfOverride] = useState<boolean>(defaultIncludePdf);
  const [recipientPreviewTargets, setRecipientPreviewTargets] = useState<ResolveWorkflowRecipientPreviewResponse['targets']>([]);
  const [recipientPreviewLoading, setRecipientPreviewLoading] = useState(false);
  const [recipientPreviewError, setRecipientPreviewError] = useState<string | null>(null);
  const [recipientOverrides, setRecipientOverrides] = useState<Record<string, WorkflowRecipientEmailGroups>>({});
  const [recipientDrafts, setRecipientDrafts] = useState<Record<string, { value: string; blurred: boolean }>>({});
  const previewRequestIdRef = useRef(0);
  const formTypeId = formType.data?.docId;
  const pdfExportOptions = useMemo(() => {
    const visibilityTypes = getVisibilityTypesForFormType(formTypeId);
    return visibilityTypes.filter((option) => option.isPdfExport === true || option.id === 'pdfview');
  }, [formTypeId, getVisibilityTypesForFormType]);
  const defaultPdfExportMode: VisibilityTypeId = useMemo(
    () => pdfExportOptions[0]?.id ?? 'pdfview',
    [pdfExportOptions]
  );
  const [pdfVisibilityMode, setPdfVisibilityMode] = useState<VisibilityTypeId>(
    sendAction?.email.pdfVisibilityExportMode
      ?? savePdfToFormAction?.pdfVisibilityExportMode
      ?? defaultPdfExportMode
  );

  /**
   * Visibility the export portal renders the PDF at (SYD-1948). Mirrors the
   * send-time selection reactively so the portal is always primed at the right
   * view without mutating the on-screen visibility: the email-PDF override when
   * set, otherwise the save-PDF action's per-field or configured view.
   */
  const pdfRenderVisibility = useMemo<VisibilityTypeId>(() => {
    if (includePdfOverride) return pdfVisibilityMode;
    const userVisParam = savePdfToFormAction?.visibilityParameterId;
    const userVisValue = userVisParam
      ? ((formValues as Record<string, unknown> | undefined)?.[userVisParam] as VisibilityTypeId | undefined)
      : undefined;
    return userVisValue ?? savePdfToFormAction?.pdfVisibilityExportMode ?? defaultPdfExportMode;
  }, [includePdfOverride, pdfVisibilityMode, savePdfToFormAction, formValues, defaultPdfExportMode]);

  const { generateXlsxAttachment, tableOptions, visibilityGate: xlsxVisibilityGate } = useXlsxAttachment({ visibilityModeOverride: pdfVisibilityMode });
  const [includeXlsxOverride, setIncludeXlsxOverride] = useState<boolean>(defaultIncludeXlsx);
  const includeXlsx = includeXlsxOverride && tableOptions.length > 0;

  /**
   * A run builds a PDF whenever the operator asked for one *or* a `savePdfToForm`
   * action does, so an unchecked Include PDF does not mean no PDF is rendered.
   * Read at render as well as in `handleOk`, because it decides which export views
   * the split-column pre-flight has to cover.
   */
  const needsPdf = includePdfOverride || !!savePdfToFormAction;

  /**
   * The export views this send actually renders at. They are two different views,
   * not one: `LayoutUI` hides tables per mode, the PDF portal renders at
   * `pdfRenderVisibility`, and the XLSX exports at `pdfVisibilityMode`. The two
   * diverge whenever Include PDF is off and a `savePdfToForm` action carries its
   * own export mode — `needsPdf` is still true then.
   */
  const exportVisibilityModes = useMemo<VisibilityTypeId[]>(
    () => [
      ...(needsPdf ? [pdfRenderVisibility] : []),
      ...(includeXlsx ? [pdfVisibilityMode] : []),
    ],
    [needsPdf, pdfRenderVisibility, includeXlsx, pdfVisibilityMode],
  );
  const exportTableIds = useVisibleTableIds(exportVisibilityModes);

  /**
   * The pre-flight for a fan-out send: every table any of this send's documents
   * renders has to carry the column the workflow splits by, or
   * `resolveSplitForTable` filters nothing and each recipient's document carries
   * every recipient's rows. Evaluated over the union of the export views, so a
   * table that only the PDF's view renders is checked even when the XLSX's view
   * hides it.
   */
  const { loading: splitColumnsLoading, warnings: splitColumnWarnings } = useSplitColumnWarnings({
    split: perTargetSplit,
    tableIds: exportTableIds,
    formTypeLabel: formType.data?.description?.shortLabel ?? formTypeId,
  });

  /**
   * The other fan-out pre-flight: per selected target, how many rows belong to it
   * and survive the ticked selection and the grid's column filters, across every
   * table this send renders.
   * Deliberately a second hook rather than a branch of the one above — that one
   * is a configuration gate that blocks the whole run, this one a per-recipient
   * exclusion. Both subscribe `tableTypes/{tableId}`; the store registry dedupes
   * the pair into one listener.
   */
  const {
    loading: recipientRowsLoading,
    unreadableTableLabels,
    rowCountByTargetId,
  } = useRecipientRowCounts({
    split: perTargetSplit,
    targetIds: selectedTargetIds,
    tableIds: exportTableIds,
    parentPath: formId && versionId ? `forms/${formId}/formVersions/${versionId}` : undefined,
  });

  /**
   * The targets this run would mail a document with none of their rows in it.
   * One memoized set for the Run gate, the Preview PDF menu and `handleOk`, so
   * the three cannot disagree on who is excluded — and a `Set` identity stable
   * enough to sit in the preview menu's `useMemo` deps.
   *
   * Empty on missing evidence, never on doubt: while the count is loading, while
   * any table's rows are unreadable, while a rendered table has no split column,
   * and when this send renders no table at all (its documents are then the same
   * for every recipient, so there is nothing to judge). The gate reasons cover
   * those states instead.
   *
   * The split-column case is the one that misleads rather than merely withholds:
   * `resolveSplitForTable` returns `null` for a table without the column, that
   * table contributes zero to every target, and a target whose rows live only
   * there would be named as empty on the advisory line and in the preview menu
   * while the banner says the configuration is broken and no run is possible.
   */
  const emptyTargetIds = useMemo(
    () => new Set(
      perTargetSplit
        && exportTableIds.length > 0
        && !recipientRowsLoading
        && unreadableTableLabels.length === 0
        && splitColumnWarnings.length === 0
        ? selectedTargetIds.filter((targetId) => rowCountByTargetId[targetId] === 0)
        : [],
    ),
    [
      perTargetSplit,
      exportTableIds,
      recipientRowsLoading,
      unreadableTableLabels,
      splitColumnWarnings,
      selectedTargetIds,
      rowCountByTargetId,
    ],
  );

  /** The excluded targets as the operator named them, for the advisory line under the Export view row. */
  const emptyTargetLabels = useMemo(
    () => selectedTargetIds.filter((targetId) => emptyTargetIds.has(targetId)).map(labelForTarget),
    [selectedTargetIds, emptyTargetIds, labelForTarget],
  );

  /**
   * Everything wrong with the configuration in one list: the value sources an
   * action names and cannot resolve, plus the split columns a fan-out send needs
   * and the tables do not have. Lives here rather than beside the other memos
   * because the pre-flight it folds in depends on the export views, which are not
   * settled until the PDF and XLSX toggles are.
   */
  const configWarnings = useMemo(
    () => [
      ...collectConfigWarnings(workflow.actions ?? [], sourceFormVersionFields, sourceFormVersionEntityReferences),
      ...splitColumnWarnings,
    ],
    [workflow.actions, sourceFormVersionFields, sourceFormVersionEntityReferences, splitColumnWarnings],
  );

  const formAttachments = formEntityData?.attachments;
  const { shareMultipleVersions } = useAttachments({
    value: formAttachments,
    onError: (error) => messageApi.error(error.message),
    onSuccess: (message) => messageApi.success(message),
  });
  const { ensureFilesShared } = useFileSharing({
    shareMultipleVersions,
    onError: (msg: string) => messageApi.error(msg),
    onLoading: (msg: string) => messageApi.loading(msg),
  });
  const pdfVisibilitySelectOptions = useMemo(
    () =>
      pdfExportOptions.map((option) => ({
        value: option.id,
        label: option.shortLabel ?? option.id,
      })),
    [pdfExportOptions]
  );
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [previewTargetId, setPreviewTargetId] = useState<string | null>(null);

  /**
   * The documents a fan-out run would build, in run order. `selectedTargetIds` is
   * the same form value `handleOk` fans out over — `perTargetSplit` exists only
   * when `splitScope.parameterId` *is* `targetsParameterId` — so the preview list
   * cannot drift from the set that gets emailed. Every selected target is here:
   * its scope comes from the workflow's target source, which is declared, not
   * resolved, so there is never a target the preview cannot show (SYD-2412).
   */
  const previewTargets = useMemo(() => {
    if (!perTargetSplit) return [];
    return selectedTargetIds.map((targetId) => ({
      targetId,
      label: labelForTarget(targetId),
      splitQuery: createSplitQueryFromTargetSource({ targetSource: perTargetSplit, targetId }),
    }));
  }, [perTargetSplit, selectedTargetIds, labelForTarget]);

  const previewTarget = previewTargets.find((entry) => entry.targetId === previewTargetId);
  const previewSplitQuery = previewTarget?.splitQuery;
  /**
   * An excluded target's document is never on screen as a preview: the run will
   * not send it, and a preview of it would tell the operator the opposite.
   */
  const previewTargetExcluded = previewTarget !== undefined && emptyTargetIds.has(previewTarget.targetId);
  const showPdfPreview = isPdfPreviewOpen
    && (!perTargetSplit || (previewTarget !== undefined && !previewTargetExcluded));

  /**
   * The open flag follows the modal off screen. Untick the last of a previewed
   * target's rows and the preview closes because the target is now excluded; left
   * set, the flag would reopen it unbidden the moment a row is ticked again.
   * Adjusted during render rather than in an effect — React re-renders before it
   * paints, and the condition is false on that second pass.
   */
  if (isPdfPreviewOpen && previewTargetExcluded) setIsPdfPreviewOpen(false);

  const openPreviewForTarget = useCallback((targetId: string) => {
    setPreviewTargetId(targetId);
    setIsPdfPreviewOpen(true);
  }, []);

  const previewTargetMenuItems = useMemo<MenuProps['items']>(
    () => previewTargets.map((entry) => emptyTargetIds.has(entry.targetId)
      ? { key: entry.targetId, label: `${entry.label} — no rows`, disabled: true }
      : { key: entry.targetId, label: entry.label }),
    [previewTargets, emptyTargetIds],
  );

  /**
   * One button, two wrappers: past a single target the menu picks which of the
   * declared per-target documents to open, and otherwise a tooltip carries the
   * reason it is disabled. The click handler stays inert while the menu is the
   * trigger, so a `Dropdown` press cannot also open the first target behind it.
   * The wrapped control is built once as `previewControl` and rendered in one of
   * two mutually exclusive slots. It sits in the send block's Export view row only
   * while `includePdfOverride` holds, because that is the one state in which
   * `pdfRenderVisibility` *is* the Select's `pdfVisibilityMode`; a button next to a
   * Select it does not obey tells the operator the view they picked is the view
   * they are about to read. Every other run that builds a PDF — Include PDF off
   * with a `savePdfToForm` action, or no send action at all — gets the row of its
   * own under the parameter form.
   *
   * An empty `pdfVisibilitySelectOptions` is the one disabling reason that is
   * neither in flight nor about the selection: this form type declares no PDF
   * export view, so no document exists to render at any scope. It reads second, so
   * the in-flight guard below keeps its precedence, and above the configuration
   * and selection branches, whose advice cannot be acted on while there is no view
   * to render at.
   *
   * `splitColumnsLoading` comes first for the reason Run gates on it: the
   * pre-flight's warnings are empty while its table types are in flight, so a
   * press landing in that window would open the modal at a scope nothing has
   * verified and show every recipient's rows as this one's.
   *
   * The row-count branches carry the same logic one step further: a recipient the
   * run would exclude has no document to preview, and while the count is in
   * flight it is not yet known which recipients those are. Past one target the
   * menu already disables them individually, so the tooltip only has to speak for
   * the single-target case. Both sit *below* the missing-selection branch: with
   * nothing selected there is no recipient to check, and the actionable reason is
   * the one the operator needs to read.
   */
  const previewTargetsMenuOpen = perTargetSplit !== undefined && previewTargets.length > 1;
  const previewUnavailableReason = splitColumnsLoading
    ? SPLIT_COLUMN_CHECK_PENDING
    : pdfVisibilitySelectOptions.length === 0
      ? 'This form type has no PDF export view configured.'
      : splitColumnWarnings.length > 0
        ? 'Resolve the configuration warnings to preview'
        : perTargetSplit !== undefined && previewTargets.length === 0
          ? `Select ${perTargetSplit.label} to preview`
          : recipientRowsLoading
            ? RECIPIENT_ROWS_CHECK_PENDING
            : previewTargets.length === 1 && emptyTargetIds.has(previewTargets[0].targetId)
              ? `${previewTargets[0].label} has no rows in their documents, so nothing would be sent to them.`
              : undefined;
  const previewDisabled = pdfVisibilitySelectOptions.length === 0 || previewUnavailableReason !== undefined;

  const handlePreviewClick = useCallback(() => {
    if (!perTargetSplit) {
      setIsPdfPreviewOpen(true);
      return;
    }
    if (previewTargets.length !== 1) return;
    openPreviewForTarget(previewTargets[0].targetId);
  }, [perTargetSplit, previewTargets, openPreviewForTarget]);

  const previewButton = (
    <Button
      type="default"
      onClick={handlePreviewClick}
      disabled={previewDisabled}
      aria-label="workflow-preview-pdf"
    >
      Preview PDF
    </Button>
  );
  const previewControl = previewTargetsMenuOpen ? (
    <Dropdown
      trigger={['click']}
      menu={{ items: previewTargetMenuItems, onClick: ({ key }) => openPreviewForTarget(key) }}
      disabled={previewDisabled}
    >
      {previewButton}
    </Dropdown>
  ) : (
    <Tooltip title={previewUnavailableReason}>{previewButton}</Tooltip>
  );

  const { generatePdfAttachment, isLoadingParams, paramsError, portalNode, portalReady } = usePdfAttachment({ formNumber, open: true, visibilityMode: pdfRenderVisibility });

  /**
   * Email templates resolve against the *selected* form — the form the user is
   * looking at when they open the sidebar. The workflow's send action may target
   * a different source form, but the email a user is composing is about the form
   * in front of them. The context hook reads the current `formVersion` from
   * `useFormStore`, deliberately not mixing in `sourceFormVersionFields`.
   */
  const templateContext = useEmailTemplateContext(formNumber);
  const {
    entries: templateEntries,
    initialTemplate,
    initialSubject,
    initialMessage,
    initialSignature,
    selectOptions: templateSelectOptions,
    resolveFor: resolveForTemplate,
  } = useEmailTemplateResolution(orgData?.emailTemplates, templateContext);
  const fallbackSubject = formNumber ?? '';
  const subjectDefault = initialSubject || fallbackSubject;
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(initialTemplate?.id);

  const [subjectOverride, setSubjectOverride] = useState(subjectDefault);
  const [messageOverride, setMessageOverride] = useState(initialMessage);
  const [signatureOverride, setSignatureOverride] = useState(initialSignature);
  const [selectedFileLinks, setSelectedFileLinks] = useState<ShareableFile[]>([]);

  const [runState, setRunState] = useState<WorkflowRunState>({ phase: 'idle' });
  /**
   * Same-tick guard on the Run button, and nothing else: `setRunState` is not
   * synchronous, so two clicks inside one tick both read `idle` off the committed
   * phase. Written only in the click handler and where its async body settles;
   * every other decision reads the phase derived below.
   */
  const dispatchInFlightRef = useRef(false);
  /**
   * How many runs have been dispatched from this sidebar. Bumped with the in-flight
   * ref, and read by `fetchRecipientPreview` to tell a preview it started before the
   * current run from one that belongs to it: `dispatchInFlightRef` is already false
   * by the time a post-run resolution lands, so it cannot make that distinction.
   */
  const dispatchGenerationRef = useRef(0);
  /**
   * Whether a stop request that the server took is outstanding on the dispatch now in
   * flight. A ref rather than state because nothing renders from it: it is read once,
   * where the outcome lands, to tell a run that finished on its own from one that
   * finished while the operator was asking it to end — the second is still a success,
   * and the banner says so along with the fact that the ask arrived too late. A
   * rejected `onStop` clears it again: that ask never reached the run (SYD-2443).
   */
  const stopPressedRef = useRef(false);

  /**
   * The operator-editable state that lives outside the antd Form. A `done` run is
   * only still the current one while this matches what it was dispatched with; the
   * Form's own edits arrive through `onValuesChange` instead, because
   * `form.setFieldsValue` — which this component calls to auto-select options and
   * to clear gated parameters — notifies `Form.useWatch` but not `onValuesChange`,
   * so a watched-value hash would unlatch Done with no operator action.
   *
   * Read off the ref *after* `onConfirm` resolves, never closed over: the
   * `handleOk` that ran was built at click time, so every value it captured is the
   * pre-click one and a mid-flight edit would be invisible to it.
   */
  const composeSignature = JSON.stringify([
    emailSenderModeOverride,
    copySenderOverride,
    includePdfOverride,
    recipientOverrides,
    Object.entries(recipientDrafts).map(([draftKey, draft]) => [draftKey, draft.value]),
    pdfVisibilityMode,
    includeXlsxOverride,
    selectedTemplateId,
    subjectOverride,
    messageOverride,
    signatureOverride,
    selectedFileLinks,
  ]);
  const composeSignatureRef = useRef(composeSignature);
  composeSignatureRef.current = composeSignature;

  const completedRun = runState.phase === 'done' && runState.signature === composeSignature
    ? runState.outcome
    : undefined;
  const runPhase: WorkflowRunPhase = runState.phase === 'running'
    ? 'running'
    : completedRun
      ? 'done'
      : 'idle';
  const isRunning = runPhase === 'running';
  /**
   * The banner's outcome, whichever end the last dispatch came back from. A failure
   * outlives the phase it was reported in — the button is back on Run, and the
   * operator needs to keep reading why while they fix the cause. A stop outlives it
   * the same way, and for the same reason: it names how far the run got.
   */
  const reportedOutcome: WorkflowRunOutcome | undefined = completedRun
    ?? (runState.phase === 'idle' ? runState.report : undefined);

  const attachmentOptionsByParamId = useMemo(() => {
    const next: Record<string, Array<{ value: string; label: string }>> = {};
    for (const param of params) {
      if (param.type !== 'attachmentSelect') continue;
      next[param.parameterId] = buildAttachmentSelectOptions(formAttachments, param.allowedMimeTypes);
    }
    return next;
  }, [formAttachments, params]);

  /**
   * A target whose recipient resolution is not ready stays selectable: its
   * per-target document is still worth previewing, and only the send needs an
   * address. The label suffix is therefore the sole signal that one is missing,
   * and the cue to type one into the recipient panel; `handleOk` drops the
   * target only if none was typed (SYD-2302, SYD-2414).
   */
  const formatResolvedOption = (opt: { value: string; label: string; secondaryLabel?: string; status: string }): ResolvedOption => {
    const suffix = opt.status === 'missingPOC'
      ? ' (Missing POC)'
      : opt.status === 'missingEmail'
        ? ' (Missing email)'
        : '';
    const secondary = opt.secondaryLabel ? ` — ${opt.secondaryLabel}` : '';
    return {
      value: opt.value,
      label: `${opt.label}${suffix}${secondary}`,
    };
  };

  /**
   * `relationshipTree` is the only `WorkflowTargetSource` variant read out of the
   * source form version's `fields` client-side; `userField` and `tableColumn`
   * resolve entirely server-side. So it alone makes the fetch wait for the
   * listener in `useWorkflowSourceFormVersion` to deliver its first snapshot,
   * which under `destroyOnHidden` is cold on every open.
   *
   * Readiness keys on the store settling, not on `fields` being present. A
   * settled source document that yields no `fields` is a missing document, not a
   * slow one — waiting on it would stall forever and silently. Letting it through
   * lands it on the `relationshipTree` throw below, which is what renders the
   * error and its Retry button.
   *
   * The field types settle alongside it, because a tree field's type is an
   * organization's own and arrives after the built-in ones: the fetch tells a
   * never-filled tree from a `fieldId` naming no field by that list.
   */
  const needsSourceFormVersionFields = useMemo(
    () => params.some((p) => p.type === 'resolvedSelect' && p.targetSource.sourceType === 'relationshipTree'),
    [params],
  );
  const sourceFormVersionFieldsReady = !needsSourceFormVersionFields || (!sourceFormVersionLoading && !fieldTypesLoading);

  /**
   * Every `relationshipTree` target parameter whose option list settled empty,
   * against the sentence naming the tree it read and found nothing on. One memo
   * for the field's help line and for the Run gate, so the two cannot disagree
   * about which lists are empty (SYD-2687).
   *
   * Silent on missing evidence, never on doubt: a list still arriving, a list
   * that failed — the error and its Retry say that — and a list waiting on its
   * own `filterBy` gate are all states with no answer yet. A parameter with no
   * entry at all is the fourth: the fetch runs in an effect, so the render that
   * flips readiness commits before it, and reading a missing entry as empty
   * would flash the notice at every tree that does carry distributors.
   */
  const emptyTreeNoticeByParamId = useMemo(() => {
    const map: Record<string, string> = {};
    if (resolvedOptionsLoading || resolvedOptionsError || !sourceFormVersionFieldsReady) return map;
    for (const p of params) {
      if (p.type !== 'resolvedSelect' || p.targetSource.sourceType !== 'relationshipTree') continue;
      if (p.filterBy && gatingValueByParamId[p.parameterId] == null) continue;
      const resolved = resolvedOptionsByParamId[p.parameterId];
      if (resolved === undefined || resolved.length > 0) continue;
      map[p.parameterId] = `No ${p.label} on this form's ${treeFieldLabel(p.targetSource.fieldId)}.`;
    }
    return map;
  }, [
    params,
    resolvedOptionsByParamId,
    resolvedOptionsLoading,
    resolvedOptionsError,
    sourceFormVersionFieldsReady,
    gatingValueByParamId,
    treeFieldLabel,
  ]);

  /**
   * The first required target list a tree left empty, as the run's own refusal.
   * A list with nothing in it cannot be filled in, so Run is refused before the
   * click and says what to add — the operator otherwise reads antd's `Required`
   * under an empty dropdown as an omission of their own (SYD-2687). It says
   * `first` rather than `again`, because the run it refuses never happened.
   */
  const emptyTreeGateReason = useMemo(() => {
    const blocking = params.find((p) => p.required && emptyTreeNoticeByParamId[p.parameterId] !== undefined);
    return blocking
      ? `${emptyTreeNoticeByParamId[blocking.parameterId]} Add one to the tree on the form first.`
      : undefined;
  }, [params, emptyTreeNoticeByParamId]);

  const fetchResolvedOptions = useCallback(async () => {
    const resolvedParams = params.filter(
      (p): p is Extract<WorkflowUserParameter, { type: 'resolvedSelect' }> => p.type === 'resolvedSelect'
    );
    const tableColumnParams = params.filter(
      (p): p is Extract<WorkflowUserParameter, { type: 'tableColumnSelect' }> => p.type === 'tableColumnSelect'
    );
    const rowCellParams: Array<{ param: WorkflowUserParameter; columnId: string; skipAutoFill: boolean }> = [];
    for (const p of params) {
      if (p.type === 'text' || p.type === 'select') {
        const src = p.defaultValueSource;
        if (src?.sourceType === 'rowCellValue') {
          rowCellParams.push({ param: p, columnId: src.columnId, skipAutoFill: false });
        }
        continue;
      }
      if (p.type === 'number') {
        const src = p.maxSource;
        if (src?.sourceType === 'rowCellValue') {
          rowCellParams.push({ param: p, columnId: src.columnId, skipAutoFill: true });
        }
      }
    }
    const skipAutoFillParamIds = new Set(rowCellParams.filter((r) => r.skipAutoFill).map((r) => r.param.parameterId));
    if (resolvedParams.length === 0 && tableColumnParams.length === 0 && rowCellParams.length === 0) return;
    if (!sourceFormId || !sourceVersionId) return;
    if (needsSourceFormVersionFields && sourceFormVersionLoading) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setResolvedOptionsLoading(true);
    setResolvedOptionsError(null);

    try {
      const resolvedPromises = resolvedParams.map(async (p) => {
        const { targetSource } = p;

        if (targetSource.sourceType === 'userField') {
          const resp = await resolveWorkflowTargets({
            formId: sourceFormId,
            versionId: sourceVersionId,
            targetSource,
            recipientResolver: p.recipientResolver,
          });
          return {
            paramId: p.parameterId,
            options: resp.options.map(formatResolvedOption),
          };
        }

        if (targetSource.sourceType === 'tableColumn') {
          let filter: { columnId: string; value: string } | undefined;
          if (p.filterBy) {
            const gatingValue = gatingValueByParamId[p.parameterId];
            if (gatingValue == null) {
              return { paramId: p.parameterId, options: [] };
            }
            filter = { columnId: p.filterBy.columnId, value: gatingValue };
          }
          const resp = await resolveWorkflowTargets({
            formId: sourceFormId,
            versionId: sourceVersionId,
            targetSource,
            recipientResolver: p.recipientResolver,
            filter,
          });
          return {
            paramId: p.parameterId,
            options: resp.options.map(formatResolvedOption),
          };
        }

        const raw = sourceFormVersionFields?.[targetSource.fieldId]?.value;
        /**
         * A tree field the operator has never filled in is stored by no field at all
         * on the version, which is an empty tree rather than a failure — the same
         * empty option list the branch below gives a tree holding only contractors,
         * and the field says which of the two it is (SYD-2687). A settled version
         * with no `fields` at all is still the missing source document the throw
         * below reports, and so is a `fieldId` naming no field type, which is the
         * workflow's configuration error rather than an empty tree.
         */
        if (sourceFormVersionFields && raw === undefined && fieldTypes[targetSource.fieldId]) {
          return { paramId: p.parameterId, options: [] };
        }
        if (!isRelationshipTreeValue(raw)) {
          throw new Error('Relationship tree value is missing or invalid');
        }

        const targetIds = dedupeStrings(
          collectRelationshipTreeEntityRecordIds({
            value: raw,
            entityOrGroupIds: targetSource.entityOrGroupIds,
          })
        );

        /**
         * A tree carrying no node of the parameter's own entities has no target to
         * resolve, and `localResolveWorkflowTargets` rejects an empty `targetIds` as a
         * malformed argument before it reads anything — the operator saw "Invalid
         * targetIds [400]" beside a Retry that could only repeat it (SYD-2682). An
         * empty option list is the answer, the same one the `filterBy` branches above
         * give when their gate is unset, and the send's own pre-flight is what says no
         * targets are selected.
         */
        if (targetIds.length === 0) {
          return { paramId: p.parameterId, options: [] };
        }

        const resp = await resolveWorkflowTargets({
          formId: sourceFormId,
          versionId: sourceVersionId,
          targetIds,
          recipientResolver: p.recipientResolver,
        });
        return {
          paramId: p.parameterId,
          options: resp.options.map(formatResolvedOption),
        };
      });

      const tableColumnPromises = tableColumnParams.map(async (p) => {
        let filter: { columnId: string; value: string } | undefined;
        if (p.filterBy) {
          const gatingValue = gatingValueByParamId[p.parameterId];
          if (gatingValue == null) {
            return { paramId: p.parameterId, options: [] };
          }
          filter = { columnId: p.filterBy.columnId, value: gatingValue };
        }
        const resp = await resolveTableColumnValues({
          formId: sourceFormId,
          versionId: sourceVersionId,
          tableId: p.tableId,
          columnId: p.columnId,
          selectedRowIds: p.scope === 'selectedRows' ? tableScope?.selectedRowIds : undefined,
          filter,
          splitFilter: tableScope?.splitFilter,
        });
        return {
          paramId: p.parameterId,
          options: resp.options,
        };
      });

      const rowCellPromises = triggerTableId
        ? rowCellParams.map(async ({ param, columnId }) => {
          const resp = await resolveTableColumnValues({
            formId: sourceFormId,
            versionId: sourceVersionId,
            tableId: triggerTableId,
            columnId,
            selectedRowIds: tableScope?.selectedRowIds,
            splitFilter: tableScope?.splitFilter,
          });
          return {
            paramId: param.parameterId,
            options: resp.options,
          };
        })
        : [];

      const resolvedResults = await Promise.all(resolvedPromises);
      const tableColumnResults = await Promise.all(tableColumnPromises);
      const rowCellResults = await Promise.all(rowCellPromises);

      if (controller.signal.aborted) return;

      setResolvedOptionsByParamId((prev) => {
        const next = { ...prev };
        for (const r of [...resolvedResults, ...tableColumnResults, ...rowCellResults]) {
          next[r.paramId] = r.options;
        }
        return next;
      });

      if (autoSelectPendingRef.current) {
        autoSelectPendingRef.current = false;
        for (const r of resolvedResults) {
          const param = resolvedParams.find((p) => p.parameterId === r.paramId);
          if (param?.mode === 'multi') {
            form.setFieldsValue({ [r.paramId]: r.options.map((o) => o.value) });
          }
        }
      }

      for (const r of [...tableColumnResults, ...rowCellResults]) {
        if (skipAutoFillParamIds.has(r.paramId)) continue;
        if (r.options.length !== 1) continue;
        if (form.getFieldValue(r.paramId) != null) continue;
        form.setFieldsValue({ [r.paramId]: r.options[0].value });
      }

      for (const r of resolvedResults) {
        const param = resolvedParams.find((p) => p.parameterId === r.paramId);
        if (param?.mode !== 'single') continue;
        if (r.options.length !== 1) continue;
        if (form.getFieldValue(r.paramId) != null) continue;
        form.setFieldsValue({ [r.paramId]: r.options[0].value });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : 'Failed to load options';
      setResolvedOptionsError(message);
    } finally {
      if (!controller.signal.aborted) setResolvedOptionsLoading(false);
    }
  }, [params, sourceFormId, sourceVersionId, needsSourceFormVersionFields, sourceFormVersionLoading, sourceFormVersionFields, tableScope?.selectedRowIds, tableScope?.splitFilter, triggerTableId, form, gatingValueByParamId, fieldTypes]);

  const handleRetryResolvedOptions = useCallback(() => {
    void fetchResolvedOptions();
  }, [fetchResolvedOptions]);

  const fetchRecipientPreview = useCallback(async () => {
    if (!showRecipientPreview || !sendAction) {
      setRecipientPreviewTargets([]);
      setRecipientOverrides({});
      setRecipientPreviewError(null);
      return;
    }
    if (!sourceFormId || !sourceVersionId) return;
    if (selectedTargetIds.length === 0) {
      setRecipientPreviewTargets([]);
      setRecipientOverrides({});
      setRecipientPreviewError(null);
      return;
    }

    const nextId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = nextId;
    const dispatchGeneration = dispatchGenerationRef.current;
    setRecipientPreviewLoading(true);
    setRecipientPreviewError(null);

    try {
      const resp = await resolveWorkflowRecipientPreview({
        formId: sourceFormId,
        versionId: sourceVersionId,
        targetIds: selectedTargetIds,
        recipientResolver: sendAction.recipientResolver,
      });
      if (previewRequestIdRef.current !== nextId) return;
      setRecipientPreviewTargets(resp.targets);
      /**
       * An entry already held wins over the response: a refetch is triggered by changing the
       * target selection, and replacing wholesale would discard the addresses typed for the
       * targets that stayed. Targets that dropped out of the response are discarded by
       * rebuilding from `resp.targets`, and a target with no `recipients` gets no entry at all.
       * A still-selected target is therefore never re-read from the server for the life of the
       * drawer — the first fetch is the only one that seeds it.
       *
       * Skipped outright when a run was dispatched while this fetch was in the air.
       * `recipientOverrides` is in the compose signature, so seeding it here would move
       * the signature the run latched `done` against: the success banner disappears and
       * the button reads Run again for a send that already went out (SYD-2442). Dropping
       * the seed costs nothing — an absent override means "let the engine resolve this
       * target's recipients", which is exactly what `target.recipients` encodes — so do
       * not restore it. The resolved addresses are still shown either way.
       */
      if (dispatchGenerationRef.current === dispatchGeneration) {
        setRecipientOverrides((prev) => resp.targets.reduce((acc, target) => {
          const groups = prev[target.targetId] ?? target.recipients;
          if (!groups) return acc;
          acc[target.targetId] = groups;
          return acc;
        }, {} as Record<string, WorkflowRecipientEmailGroups>));
      }
    } catch (err) {
      if (previewRequestIdRef.current !== nextId) return;
      const message = err instanceof Error ? err.message : 'Failed to load recipients';
      setRecipientPreviewError(message);
      setRecipientPreviewTargets([]);
      if (dispatchGenerationRef.current === dispatchGeneration) {
        setRecipientOverrides({});
      }
    } finally {
      if (previewRequestIdRef.current === nextId) {
        setRecipientPreviewLoading(false);
      }
    }
  }, [
    showRecipientPreview,
    sendAction,
    sourceFormId,
    sourceVersionId,
    selectedTargetIds,
  ]);

  /**
   * Refused while a run is in flight, like every other control: this reseeds
   * `recipientOverrides`, which is in the compose signature, so a retry resolving
   * after the run would unlatch Done beside the banner for a send already gone out.
   */
  const handleRetryRecipientPreview = useCallback(() => {
    void fetchRecipientPreview();
  }, [fetchRecipientPreview]);

  useEffect(() => {
    void fetchRecipientPreview();
  }, [fetchRecipientPreview]);

  /**
   * Mount is the open transition. `destroyOnHidden` on the shell's Drawer means
   * this component exists only while the sidebar is on screen, so every default
   * `afterOpenChange(true)` used to re-apply is now the `useState` initializer
   * above it, and the close branch went with the state it used to reset. What is
   * left is the one fetch that had no other trigger — the gating-key effect below
   * skips its first pass — and aborting whatever it started.
   *
   * Keyed on readiness rather than `[]` because `useWorkflowSourceFormVersion`
   * connects its listener in this same effect pass, so a `relationshipTree`
   * parameter's fetch hits the wait-guard on the first render of every open and
   * would never be retried. Readiness is the store's settle flag, not the fields
   * themselves: it flips false→true once and then holds, so later snapshots of
   * the same document cannot refire the fetch, and a workflow that never needs
   * the fields fetches exactly once at mount. `fetchResolvedOptions` is
   * deliberately not a dependency — its identity turns over with
   * `gatingValueByParamId`, which is rebuilt from the watched form values on
   * every keystroke, and the gating effect below already owns that trigger.
   */
  useEffect(() => {
    if (sourceFormVersionFieldsReady) void fetchResolvedOptions();
    return () => abortControllerRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFormVersionFieldsReady]);

  /**
   * A `fixedValue` scope pins the run to one split option. Writing the selection
   * when `find` misses would clear it to `no-split` — form-wide state that widens
   * every ArTable, and the Run button is live while the options are still cold.
   * A permanent miss (the value has no rows) keeps whatever the user had rather
   * than silently running against everything; `formStore`'s `splitQuery` JSDoc
   * forbids repairing that from the options.
   */
  useEffect(() => {
    if (!workflow.splitScope?.fixedValue) return;
    const match = splitOptions.find((opt) => opt.actualValue === workflow.splitScope!.fixedValue);
    if (!match) return;
    formActions.setSelectedSplitOption(match);
  }, [splitOptions, workflow.splitScope]);

  const previousGatingKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const nextKey = JSON.stringify(gatingValueByParamId);
    if (previousGatingKeyRef.current === null) {
      previousGatingKeyRef.current = nextKey;
      return;
    }
    if (previousGatingKeyRef.current === nextKey) return;
    const prev = JSON.parse(previousGatingKeyRef.current) as Record<string, string | undefined>;
    previousGatingKeyRef.current = nextKey;
    const clears: Record<string, undefined> = {};
    for (const paramId of Object.keys(gatingValueByParamId)) {
      if (prev[paramId] !== gatingValueByParamId[paramId]) clears[paramId] = undefined;
    }
    if (Object.keys(clears).length > 0) form.setFieldsValue(clears);
    void fetchResolvedOptions();
  }, [gatingValueByParamId, form, fetchResolvedOptions]);

  const handleRecipientToggle = useCallback((
    targetId: string,
    role: keyof WorkflowRecipientEmailGroups,
    email: string,
    checked: boolean
  ) => {
    setRecipientOverrides((prev) => {
      const current = prev[targetId] ?? { to: [], cc: [], bcc: [] };
      const nextRoleSet = new Set(current[role]);
      if (checked) {
        nextRoleSet.add(email);
      } else {
        nextRoleSet.delete(email);
      }
      return {
        ...prev,
        [targetId]: {
          ...current,
          [role]: Array.from(nextRoleSet),
        },
      };
    });
  }, []);

  /**
   * Adds an address the operator typed for a target the resolver could not address. `email` is
   * already normalized and checked against every role of that target at the call site, which is
   * where the Add button's own disabled state is computed from — the two cannot disagree. The
   * address lives in the run's overrides only; nothing is written back to the entity's
   * recipients tree.
   */
  const handleRecipientAdd = useCallback((
    targetId: string,
    role: keyof WorkflowRecipientEmailGroups,
    email: string,
  ) => {
    setRecipientOverrides((prev) => {
      const current = prev[targetId] ?? { to: [], cc: [], bcc: [] };
      const nextRoleSet = new Set(current[role]);
      nextRoleSet.add(email);
      return {
        ...prev,
        [targetId]: {
          ...current,
          [role]: Array.from(nextRoleSet),
        },
      };
    });
    setRecipientDrafts((prev) => ({ ...prev, [`${targetId}-${role}`]: { value: '', blurred: false } }));
  }, []);

  /**
   * Builds the payload and dispatches it. `null` means nothing was dispatched —
   * either a gate refused the run and has already said so, or `onConfirm` did.
   * A rejected `validateFields` is one of those refusals and stays silent: antd
   * has already marked the offending fields.
   */
  const handleOk = async (): Promise<WorkflowRunOutcome | null> => {
    const validated = await form.validateFields().catch(() => null);
    if (validated === null) return null;

    /**
     * The antd field keeps the Dayjs its DatePicker natively holds; the wire value is
     * built once, here, so no reverse mapping has to undo a zone conversion for display
     * (SYD-1785). Everything below reads `values`.
     */
    const values: Record<string, unknown> = Object.fromEntries(
      Object.entries(validated).map(([parameterId, value]) => [
        parameterId,
        dateParameterIds.has(parameterId) && dayjs.isDayjs(value)
          ? dateOnlyTimestamp(value)
          : value,
      ]),
    );

    let sendOverrides: WorkflowSendOverrides | undefined;
    if (sendAction) {
      const finalFileLinks = await ensureFilesShared(selectedFileLinks);
      setSelectedFileLinks(finalFileLinks);
      const sharedFiles = finalFileLinks.filter(f => f.isShared && f.shareUrl);
      const { html: fileLinksHtml } = createFileLinksSection(sharedFiles);

      const baseSendFields = {
        subject: subjectOverride,
        message: messageOverride,
        signature: signatureOverride,
        pdfVisibilityExportMode: pdfVisibilityMode,
        ...(fileLinksHtml ? { fileLinksHtml } : undefined),
      };

      if (showRecipientPreview) {
        sendOverrides = {
          ...baseSendFields,
          byTargetId: recipientOverrides,
          includePdf: includePdfOverride,
          includeXlsx,
          emailSenderMode,
          copySender,
        };
      } else {
        sendOverrides = { ...baseSendFields, byTargetId: {}, emailSenderMode, copySender };
      }
    }

    const nextValues = sendOverrides ? { ...values, __sendOverrides: sendOverrides } : values;

    if (needsPdf) {
      if (!portalReady) {
        messageApi.error('PDF export portal is not ready.');
        return null;
      }
      if (isLoadingParams) {
        messageApi.warning('Organization details are still loading, please wait a moment and try again.');
        return null;
      }
      if (paramsError) {
        messageApi.error(`Error with PDF parameters: ${paramsError}`);
        return null;
      }
    }
    if (includeXlsx && xlsxVisibilityGate) {
      messageApi[xlsxVisibilityGate.severity](xlsxVisibilityGate.message);
      return null;
    }

    const filenameParts = [formNumber, formTitle]
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
    const filenameBase = filenameParts.length > 0
      ? filenameParts.join('-').replace(/[^a-zA-Z0-9-_]+/g, '-')
      : 'export';

    /**
     * A target with no address is selectable on purpose — its document is still
     * worth previewing — so it is dropped here, at the send, and named in a
     * persistent warning while the rest go out (SYD-2302).
     *
     * The rule is the server's rule: an empty effective `to` list, never the
     * resolver status. A target the resolver reported as `missingPOC` or
     * `missingEmail` that the operator gave an address to in the recipient panel
     * has a non-empty override and survives; a `ready` target whose `to` list the
     * operator emptied does not (SYD-2414).
     *
     * A target absent from `recipientPreviewTargets` is *not* excluded. That list
     * is empty whenever the preview is off, still in flight, or errored, so
     * absence is missing evidence rather than evidence of a missing contact —
     * treating it as unsendable would silently drop every target on a slow open.
     * The server resolves recipients again at send time and records its own
     * failure for a target it cannot address.
     */
    const noEmailTargetIds = new Set(
      recipientPreviewTargets
        .filter((target) => (recipientOverrides[target.targetId]?.to ?? target.recipients?.to ?? []).length === 0)
        .map((target) => target.targetId),
    );
    /**
     * The second exclusion, on the same rule and for the same reason: a fan-out
     * recipient whose document would carry none of their rows is dropped rather
     * than mailed a blank one (SYD-2410). Read off the render closure, which is
     * the count for the selection as it stood at the click — a tick is a discrete
     * event whose render commits before this handler runs. Only a run that builds
     * a document can exclude on it; a send with neither a PDF nor an XLSX has no
     * rows to be missing.
     */
    const noRowsTargetIds = perTargetSplit && (needsPdf || includeXlsx) ? emptyTargetIds : new Set<string>();
    const rawSelectedTargets = targetsParameterId
      ? values[targetsParameterId]
      : undefined;
    const selectedTargets = (Array.isArray(rawSelectedTargets) ? rawSelectedTargets : [rawSelectedTargets])
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
    const sendableTargetIds = selectedTargets
      .filter((targetId) => !noEmailTargetIds.has(targetId) && !noRowsTargetIds.has(targetId));
    const noEmailExcluded = selectedTargets.filter((targetId) => noEmailTargetIds.has(targetId));
    const noRowsExcluded = selectedTargets.filter((targetId) => noRowsTargetIds.has(targetId));
    const noEmailLabels = noEmailExcluded.map(labelForTarget);
    const noRowsLabels = noRowsExcluded.map(labelForTarget);

    if (selectedTargets.length > 0 && sendableTargetIds.length === 0) {
      messageApi.error(
        noRowsLabels.length === 0
          ? `None of the selected ${targetsParameterLabel} has an email address, so there is nothing to send.`
          : noEmailLabels.length === 0
            ? `None of the selected ${targetsParameterLabel} has rows in their documents, so there is nothing to send.`
            : `Every selected ${targetsParameterLabel} was excluded — no email address or no rows — so there is nothing to send.`,
      );
      return null;
    }
    if (noEmailLabels.length > 0) {
      messageApi.warning({
        key: EXCLUDED_NO_EMAIL_MESSAGE_KEY,
        content: `Excluded from this run — no email address: ${noEmailLabels.join(', ')}.`,
        duration: 0,
      });
    }
    if (noRowsLabels.length > 0) {
      messageApi.warning({
        key: EXCLUDED_NO_ROWS_MESSAGE_KEY,
        content: `Excluded from this run — no rows in their documents: ${noRowsLabels.join(', ')}.`,
        duration: 0,
      });
    }
    /**
     * Anything excluded — no address or no rows — has to leave the run's own
     * parameters too. Naming an excluded target there while withholding its
     * document makes the server mail them a message with no attachment.
     */
    const runValues = targetsParameterId
      && Array.isArray(rawSelectedTargets)
      && sendableTargetIds.length < selectedTargets.length
      ? { ...nextValues, [targetsParameterId]: sendableTargetIds }
      : nextValues;

    /**
     * Puts the browser's own exclusions back into the run's counts. Every excluded
     * recipient was dropped before the dispatch, so the server never saw them and
     * its `skipped` is short by exactly that many — the banner read `Skipped: 0`
     * beside a warning naming the recipients it had just dropped (SYD-2410).
     *
     * They count as `skipped` rather than `failed` because nothing failed: the
     * browser declined to send, for a reason it can state, and the run itself was
     * fine.
     *
     * The two exclusion sets overlap — a target can have neither an address nor a
     * row of its own — so the summary counts and details their *union*, one entry
     * per recipient. The toasts above deliberately do not: they name causes, and a
     * dual-cause target belongs under both. This names recipients, and counting one
     * twice makes `skipped` exceed the selection.
     *
     * No entry carries a `status`. The exclusion rule is an empty effective `to`
     * list, never the resolver's status, so stamping `missingEmail` on a `ready`
     * target whose list the operator emptied would report the wrong cause; `reason`
     * says it, and the banner prefers `reason` anyway.
     *
     * An outcome with no summary is left exactly as it is. The banner shows no
     * counts at all in that case, and minting a summary here would have to invent a
     * `sent` the browser never observed. The persistent warnings still name the
     * excluded recipients.
     *
     * A stopped run's summary is folded exactly like a completed one's. The browser
     * dropped those recipients before the dispatch either way, so the server's
     * `skipped` is short by the same count whether the run ran to the end or was
     * asked to stop partway (SYD-2443).
     */
    const withExcludedRecipients = (outcome: WorkflowRunOutcome | null): WorkflowRunOutcome | null => {
      if (outcome === null || outcome.status === 'error' || outcome.summary === undefined) return outcome;
      const noRowsOnlyExcluded = noRowsExcluded.filter((targetId) => !noEmailTargetIds.has(targetId));
      const excludedCount = noEmailExcluded.length + noRowsOnlyExcluded.length;
      if (excludedCount === 0) return outcome;
      return {
        ...outcome,
        summary: {
          ...outcome.summary,
          skipped: outcome.summary.skipped + excludedCount,
          details: [
            ...(outcome.summary.details ?? []),
            ...noEmailExcluded.map((targetId) => ({ targetId, reason: 'No email address' })),
            ...noRowsOnlyExcluded.map((targetId) => ({ targetId, reason: 'No rows in their document' })),
          ],
        },
      };
    };

    if (perTargetSplit && (needsPdf || includeXlsx)) {
      /**
       * A fan-out with nothing selected dispatches zero documents, which the
       * server reads as a send to nobody. The shared arm in the same state builds
       * the whole-document PDF and sends it, so the two arms have to disagree
       * loudly rather than silently.
       */
      if (sendableTargetIds.length === 0) {
        messageApi.error(`No ${targetsParameterLabel} is selected, so there is nothing to send.`);
        return null;
      }

      /**
       * Strictly sequential: `generatePdfAttachment` owns a single export portal
       * and is not reentrant, so `Promise.all` here would let one target's rows
       * be captured into another's document. Any failure aborts the whole run —
       * nothing external has happened yet, and the send is not idempotent.
       *
       * The payload ceiling is measured per iteration rather than once at the end:
       * each target costs a PDF render plus an XLSX round-trip, so a post-loop
       * check would make the operator wait through every target's work only to
       * throw all of it away.
       */
      const documentsByTargetId: Record<string, WorkflowEmailAttachment[]> = {};
      try {
        for (const targetId of sendableTargetIds) {
          const splitQuery = createSplitQueryFromTargetSource({ targetSource: perTargetSplit, targetId });
          const scopeSuffix = labelForTarget(targetId).replace(/[^a-zA-Z0-9-_]+/g, '-');
          const pdfDocument = needsPdf
            ? await generatePdfAttachment({
              formNumber,
              formTitle: `${formTitle ?? 'Form'}-${scopeSuffix}`,
              splitQuery,
            })
            : undefined;
          const xlsxDocument = includeXlsx
            ? await generateXlsxAttachment({
              selectedKeys: undefined,
              formNumber,
              filenameBase: `${filenameBase}-${scopeSuffix}`,
              splitQuery,
            })
            : undefined;
          documentsByTargetId[targetId] = [pdfDocument, xlsxDocument]
            .filter((document): document is WorkflowEmailAttachment => document !== undefined);

          const payloadBytes = Object.values(documentsByTargetId)
            .flat()
            .reduce((total, document) => total + document.content.length, 0);
          if (payloadBytes > MAX_ATTACHMENT_PAYLOAD_BYTES) {
            const builtCount = Object.keys(documentsByTargetId).length;
            messageApi.error(
              `Documents for ${builtCount} of ${sendableTargetIds.length} recipients already total ${(payloadBytes / (1024 * 1024)).toFixed(1)} MB, over the ${MAX_ATTACHMENT_PAYLOAD_BYTES / (1024 * 1024)} MB a single run can carry. Nothing was sent — run it for fewer recipients at a time.`,
            );
            return null;
          }
        }
      } catch (err) {
        messageApi.error(
          `Failed to build the per-recipient documents: ${err instanceof Error ? err.message : String(err)}. Nothing was sent.`,
        );
        return null;
      }

      return withExcludedRecipients(await onConfirm(
        { ...runValues, [perTargetSplit.parameterId]: sendableTargetIds },
        { documentsByTargetId },
      ));
    }

    /**
     * Caught for the same reason the fan-out loop above catches: a render or an
     * XLSX round-trip that throws is a run that never left the browser, and
     * neither generator resolves `null`, so it is an unambiguous failure marker.
     */
    const pdfDocument = needsPdf
      ? await generatePdfAttachment({ formNumber, formTitle: formTitle ?? 'Form' })
        .catch((err: unknown) => {
          messageApi.error(
            `Failed to build the PDF: ${err instanceof Error ? err.message : String(err)}. Nothing was sent.`,
          );
          return null;
        })
      : undefined;
    if (pdfDocument === null) return null;

    const xlsxDocument = includeXlsx
      ? await generateXlsxAttachment({ selectedKeys: undefined, formNumber, filenameBase })
        .catch((err: unknown) => {
          messageApi.error(
            `Failed to build the XLSX: ${err instanceof Error ? err.message : String(err)}. Nothing was sent.`,
          );
          return null;
        })
      : undefined;
    if (xlsxDocument === null) return null;

    const documents = [pdfDocument, xlsxDocument]
      .filter((document): document is WorkflowEmailAttachment => document !== undefined);

    return withExcludedRecipients(await onConfirm(runValues, documents.length > 0 ? { documents } : undefined));
  };

  /**
   * The only way a run starts. Synchronous down to the in-flight ref so a second
   * click in the same tick is refused before React has committed `running` — the
   * whole pre-flight inside `handleOk` used to run with the button still live.
   *
   * Only a success latches Done. An escaped throw is reported as a failure and
   * returns to idle, because a throw is no evidence that anything was dispatched:
   * `ensureFilesShared` raises its own toast and throws before `onConfirm` is
   * reached, and a quote that never left must stay one press away.
   *
   * The failure is worded by `workflowRunErrorOutcome`, the same formatter the four
   * other run sites use, so a flattened callable throw never reaches the banner as
   * `INTERNAL` (SYD-2445).
   *
   * `formStore.state.isDispatchingWorkflowRun` rides along with the ref, and for
   * the same reason it is synchronous: it disables every control that changes a
   * table's row scope, and the awaits *before* the exclusion block in `handleOk`
   * are already inside the window the documents are built from (SYD-2410).
   *
   * The in-flight ref and the lock are cleared in a `finally`: a rejection the
   * `.catch` above cannot itself report would otherwise leave the ref set and the
   * button dead until the sidebar closes, and the grid locked with no run to wait
   * for.
   */
  const handleRunClick = () => {
    if (runPhase === 'done') {
      onCancel();
      return;
    }
    if (runPhase === 'running' || dispatchInFlightRef.current) return;
    dispatchInFlightRef.current = true;
    stopPressedRef.current = false;
    formStore.state.isDispatchingWorkflowRun = true;
    messageApi.destroy(EXCLUDED_NO_EMAIL_MESSAGE_KEY);
    messageApi.destroy(EXCLUDED_NO_ROWS_MESSAGE_KEY);
    dispatchGenerationRef.current += 1;
    setRunState({ phase: 'running' });

    void (async () => {
      try {
        const outcome = await handleOk().catch((err: unknown): WorkflowRunOutcome =>
          workflowRunErrorOutcome(err, workflow.description?.shortLabel));
        if (outcome === null) {
          setRunState({ phase: 'idle' });
          return;
        }
        if (outcome.status === 'error') {
          setRunState({ phase: 'idle', report: outcome });
          return;
        }
        /**
         * A stop latches nothing: the operator asked the run to end and may need to
         * dispatch again, so the button returns to Run with the stop on the banner,
         * exactly as a failure does (SYD-2443).
         */
        if (outcome.status === 'stopped') {
          setRunState({ phase: 'idle', report: outcome });
          return;
        }
        /**
         * A success reached while Stop was pending is still a success — the run had
         * already finished when the ask landed — so it latches Done, and the sentence
         * is the only honest account of what the press did.
         */
        setRunState({
          phase: 'done',
          outcome: stopPressedRef.current
            ? { ...outcome, text: `${outcome.text} The stop arrived after the run had finished.` }
            : outcome,
          signature: composeSignatureRef.current,
        });
      } finally {
        dispatchInFlightRef.current = false;
        formStore.state.isDispatchingWorkflowRun = false;
      }
    })();
  };

  /**
   * Why Run is refused, and the status banner's warning title. Undefined exactly
   * when none of the eight gates holds, so it is also the button's disabled state.
   *
   * The empty-tree gate sits second because it is the one settled fact among
   * them: no amount of waiting puts a distributor on a tree that holds none, so
   * naming it outranks the in-flight checks below it.
   *
   * The two pre-flight gates sit above the data gates because each one's own
   * finding is empty while it is in flight: a click landing then would build
   * every per-target document without the split having been verified, or without
   * knowing which recipients have rows.
   *
   * An unreadable table is its own gate rather than a zero row count. Counting it
   * as zero would name innocent recipients as empty, which is a wrong message
   * where this is merely an unmakeable send.
   *
   * The all-empty reason names the two narrowings and promises no action: with
   * nothing ticked and no filter set, the counts can be legitimately zero because
   * the tables hold no rows carrying any selected recipient's value, and telling
   * the operator to untick rows would then be a dead end.
   */
  const runGateReason = configWarnings.length > 0
    ? 'Configuration warnings'
    : emptyTreeGateReason !== undefined
      ? emptyTreeGateReason
      : splitColumnsLoading
        ? SPLIT_COLUMN_CHECK_PENDING
        : recipientRowsLoading
          ? RECIPIENT_ROWS_CHECK_PENDING
          : unreadableTableLabels.length > 0
            ? `Could not read the rows of ${unreadableTableLabels.join(', ')}, so it is unknown which ${targetsParameterLabel} have rows. Close and reopen the sidebar to retry.`
            : perTargetSplit && selectedTargetIds.length > 0 && emptyTargetIds.size === selectedTargetIds.length
              ? `None of the selected ${targetsParameterLabel} has rows in their documents, once the ticked rows and any column filters are applied.`
              : tableScope?.totalCount === 0
                ? 'Table has no rows to submit.'
                : splitScopeUnmatched
                  ? 'The rows for the selected split are still loading, or that value matches no rows. Running now would use the previous split.'
                  : undefined;

  /**
   * Where a failed run stopped. Rows under the failure sentence rather than more
   * words inside it: this banner is the one surface an operator reads long enough
   * to copy a stage into a support message (SYD-2445). Both values are server
   * identifiers, so each goes through the map that gives it the words the workflow
   * editor uses — the rows read `Stage: sending`, not `Stage: send`.
   */
  const runFailureDetail: string[] = reportedOutcome?.status === 'error' && reportedOutcome.details
    ? [
        reportedOutcome.details.stage !== undefined
          ? `Stage: ${workflowRunStageLabel(reportedOutcome.details.stage)}`
          : undefined,
        reportedOutcome.details.actionType !== undefined
          ? `Action: ${workflowActionTypeLabel(reportedOutcome.details.actionType)}${
              reportedOutcome.details.actionIndex !== undefined
                ? ` (${reportedOutcome.details.actionIndex})`
                : ''
            }`
          : undefined,
      ].filter((row): row is string => row !== undefined)
    : [];

  /**
   * The send counts the banner shows, and how many of them were never reached. Both
   * are derived where they are read rather than carried on the outcome: `notSent` is
   * a detail status the server already stamps on every recipient a stopped send got
   * no further than, so counting it here keeps the summary one shape (SYD-2443).
   */
  const reportedSummary = reportedOutcome !== undefined && reportedOutcome.status !== 'error'
    ? reportedOutcome.summary
    : undefined;
  const notSentCount = (reportedSummary?.details ?? [])
    .filter((detail) => detail.status === 'notSent').length;

  /**
   * Why Stop is refused, on the button's own tooltip. `idle` covers two windows the
   * operator cannot tell apart from the outside: before Run, when there is nothing to
   * stop, and the browser pre-flight, when documents are still being built and nothing
   * external has happened yet. Naming the window is what keeps the disabled button from
   * reading as broken (SYD-2443).
   */
  const stopUnavailableReason = stopPhase === 'stoppable'
    ? undefined
    : stopPhase === 'stopping'
      ? 'The run is already stopping.'
      : isRunning
        ? 'Stop becomes available once the run reaches the server.'
        : 'There is no run to stop.';

  /**
   * One band above the footer for every state the run can be in, so the gate that
   * holds Run disabled, the run itself and its result never compete for space. A
   * never-run sidebar with no gate has no status, and renders nothing; a failed one
   * keeps its error beside the live Run button. `width: '100%'` because the root
   * `FlexCol stretch` aligns its children to `flex-start`.
   *
   * A stop is `warning`, never `error`: nothing went wrong, and the work that landed
   * before it is real. It reads the same counts a success does, with the recipients
   * the send never reached counted beside them.
   *
   * Each summary detail line names its target the way the operator picked it —
   * `detail.targetId` is a raw record id — and `labelForTarget` falls back to that
   * id when the parameter's options do not carry the target.
   */
  const runStatusBanner = isRunning && stopPhase === 'stopping' ? (
    <Alert
      type="info"
      title="Stopping…"
      description="The run ends after the step in progress. Emails already sent stay sent."
      style={{ width: '100%' }}
    />
  ) : isRunning ? (
    <Alert
      type="info"
      title="Running…"
      description={stopPhase === 'stoppable'
        ? 'Stop ends the run, and it is only in this sidebar. Closing the sidebar leaves the run under way. Completed steps stay done.'
        : 'Stop becomes available once the run reaches the server, and only in this sidebar. Closing the sidebar leaves the run under way.'}
      style={{ width: '100%' }}
    />
  ) : reportedOutcome ? (
    <Alert
      type={reportedOutcome.status === 'error'
        ? 'error'
        : reportedOutcome.status === 'stopped'
          ? 'warning'
          : 'success'}
      title={reportedOutcome.text}
      description={reportedOutcome.status === 'error' ? (runFailureDetail.length > 0 ? (
        <FlexCol gap={0}>
          {runFailureDetail.map((row) => (
            <Text key={row} type="secondary" style={{ fontSize: token.fontSizeSM }}>{row}</Text>
          ))}
        </FlexCol>
      ) : undefined) : reportedSummary ? (
        <FlexCol gap={0}>
          <Text>Sent: {reportedSummary.sent}</Text>
          <Text>Skipped: {reportedSummary.skipped}</Text>
          <Text>Failed: {reportedSummary.failed}</Text>
          {notSentCount > 0 && <Text>Not sent: {notSentCount}</Text>}
          {reportedSummary.details?.map((detail, idx) => (
            <Text key={idx} type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {detail.targetId !== undefined ? labelForTarget(detail.targetId) : undefined}: {detail.reason || detail.status}
            </Text>
          ))}
        </FlexCol>
      ) : undefined}
      style={{ width: '100%' }}
    />
  ) : runGateReason ? (
    <Alert
      type="warning"
      title={runGateReason}
      description={configWarnings.length > 0 ? (
        <FlexCol gap={0}>
          {configWarnings.map((warning) => (
            <Text key={warning} style={{ fontSize: token.fontSizeSM }}>{warning}</Text>
          ))}
        </FlexCol>
      ) : undefined}
      style={{ width: '100%' }}
    />
  ) : null;

  return (
    <>
      <FlexCol stretch>
        {resolvedFieldMappingPreview.length > 0 && (
          <>
            <FlexCol gap={5}>
              <Text strong>Values from document</Text>
              {resolvedFieldMappingPreview.map(({ label, value }) => (
                <FlexBox key={label} style={{ gap: 8 }}>
                  <Text type="secondary" style={{ minWidth: 120, flexShrink: 0 }}>{label}</Text>
                  <Text ellipsis>{value || '—'}</Text>
                </FlexBox>
              ))}
            </FlexCol>
            <Divider style={{ margin: 0 }} />
          </>
        )}
        {productColumnPreview.length > 0 && (
          <>
            <FlexCol gap={5}>
              <Text strong>Product columns</Text>
              {productColumnPreview.map(({ column, label, configured }) => (
                <FlexBox key={column} style={{ gap: 8 }}>
                  <Text type="secondary" style={{ minWidth: 120, flexShrink: 0 }}>{column}</Text>
                  <Text type={configured ? undefined : 'warning'}>{label}</Text>
                </FlexBox>
              ))}
            </FlexCol>
            <Divider style={{ margin: 0 }} />
          </>
        )}
        {tableScopeLabel && (
          <>
            <FlexBox style={{ gap: 8 }}>
              <Text strong>Rows</Text>
              <Text>{tableScopeLabel}</Text>
            </FlexBox>
            <Divider style={{ margin: 0 }} />
          </>
        )}
        {params.length === 0 ? (
          <Text type="secondary">No parameters required.</Text>
        ) : (
          <Form
            form={form}
            layout="vertical"
            preserve={false}
            disabled={runPhase === 'running'}
            initialValues={mergedInitialValues}
            onValuesChange={(changedValues) => {
              if (runState.phase === 'done') setRunState({ phase: 'idle' });
              if (!workflow.splitScope?.parameterId) return;
              const selectedValue = changedValues[workflow.splitScope.parameterId];
              if (typeof selectedValue !== 'string') return;
              const match = splitOptions.find((opt) => opt.actualValue === selectedValue);
              if (match) formActions.setSelectedSplitOption(match);
            }}
            style={{ width: '100%', overflowY: 'auto' }}
          >
            {params.map((param) => {
              const rules = param.required ? [{ required: true, message: 'Required' }] : [];
              const ariaLabel = `workflow-param-${workflow.docId || 'workflow'}-${param.parameterId}`;

              if (param.type === 'text') {
                const isRowCell = param.defaultValueSource?.sourceType === 'rowCellValue';
                const rowCellOptions = isRowCell ? (resolvedOptionsByParamId[param.parameterId] ?? []) : [];
                const hasConflict = isRowCell && rowCellOptions.length > 1;
                const conflictHelp = hasConflict
                  ? `Selected rows contain multiple values for ${param.label} (${rowCellOptions.map((o) => o.value).join(', ')}). Pick one to apply to all selected rows.`
                  : undefined;
                const textRules: Array<Record<string, unknown>> = [...rules];
                if (param.minLength !== undefined) {
                  textRules.push({ min: param.minLength, message: `Must be at least ${param.minLength} characters` });
                }
                if (param.pattern) {
                  try {
                    textRules.push({ pattern: new RegExp(param.pattern), message: param.patternErrorMessage ?? 'Invalid format' });
                  } catch {
                    console.warn(`[WorkflowParameterSidebar] Invalid regex pattern for ${param.parameterId}: ${param.pattern}`);
                  }
                }
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={textRules}
                    validateStatus={hasConflict ? 'warning' : undefined}
                    help={conflictHelp ?? param.helpText}
                  >
                    <Input aria-label={ariaLabel} maxLength={param.maxLength} showCount={!!param.maxLength} />
                  </Form.Item>
                );
              }

              if (param.type === 'number') {
                const numberRules: Array<Record<string, unknown>> = [...rules];
                // When maxSource resolves across multiple selected rows, the effective cap is
                // the smallest of those values — releasing 10 from a row with only 5 remaining
                // must be rejected even if a sibling row has 100.
                const resolvedValues = param.maxSource?.sourceType === 'rowCellValue'
                  ? (resolvedOptionsByParamId[param.parameterId] ?? [])
                      .map((o) => Number(o.value))
                      .filter((n) => Number.isFinite(n))
                  : [];
                const resolvedMin = resolvedValues.length > 0 ? Math.min(...resolvedValues) : NaN;
                const effectiveMax = Number.isFinite(resolvedMin) ? resolvedMin : param.max;
                if (param.min !== undefined) {
                  numberRules.push({ type: 'number', min: param.min, message: `Must be at least ${param.min}.` });
                }
                if (effectiveMax !== undefined) {
                  numberRules.push({ type: 'number', max: effectiveMax, message: `Must be at most ${effectiveMax}.` });
                }
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={numberRules}
                  >
                    <InputNumber
                      aria-label={ariaLabel}
                      min={param.min}
                      max={effectiveMax}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                );
              }

              if (param.type === 'select') {
                const isRowCell = param.defaultValueSource?.sourceType === 'rowCellValue';
                const rowCellOptions = isRowCell ? (resolvedOptionsByParamId[param.parameterId] ?? []) : [];
                const hasConflict = isRowCell && rowCellOptions.length > 1;
                const conflictHelp = hasConflict
                  ? `Selected rows contain multiple values for ${param.label} (${rowCellOptions.map((o) => o.value).join(', ')}). Pick one to apply to all selected rows.`
                  : undefined;
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                    validateStatus={hasConflict ? 'warning' : undefined}
                    help={conflictHelp ?? param.helpText}
                  >
                    <Select
                      aria-label={ariaLabel}
                      options={param.options ?? []}
                      showSearch={{ optionFilterProp: 'label' }}
                      allowClear={!param.required}
                    />
                  </Form.Item>
                );
              }

              if (param.type === 'attachmentSelect') {
                const options = attachmentOptionsByParamId[param.parameterId] ?? [];
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                    validateStatus={options.length === 0 ? 'warning' : undefined}
                    help={options.length === 0 ? 'No matching attachments available on this form.' : undefined}
                  >
                    <Select
                      aria-label={ariaLabel}
                      options={options}
                      showSearch={{ optionFilterProp: 'label' }}
                      allowClear={!param.required}
                      disabled={options.length === 0}
                    />
                  </Form.Item>
                );
              }

              if (param.type === 'resolvedSelect') {
                const options = resolvedOptionsByParamId[param.parameterId] ?? [];
                const isMulti = param.mode === 'multi';
                const awaitingGate = !!param.filterBy && gatingValueByParamId[param.parameterId] == null;
                const gatePlaceholder = awaitingGate
                  ? `Select ${gatingParamLabelByParamId[param.parameterId] ?? param.filterBy!.parameterId} first`
                  : undefined;
                /**
                 * An empty list read out of a tree is an answer, not a failure, so it
                 * says which tree it read and found nothing on — the operator otherwise
                 * reads antd's "No data" as the send being broken and searches the list
                 * for names that were never on the tree (SYD-2687). Stated the way the
                 * attachment field states its own empty list, and with the control
                 * disabled the way that field disables its own, because there is
                 * nothing in it to search for.
                 */
                const emptyTreeNotice = emptyTreeNoticeByParamId[param.parameterId];

                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                    validateStatus={resolvedOptionsError ? 'error' : undefined}
                    help={
                      resolvedOptionsError ? (
                        <FlexBox style={{ gap: 8, alignItems: 'center' }}>
                          <span>{resolvedOptionsError}</span>
                          <Button
                            type="link"
                            size="small"
                            aria-label={`retry-${param.parameterId}`}
                            onClick={handleRetryResolvedOptions}
                            disabled={isRunning}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            Retry
                          </Button>
                        </FlexBox>
                      ) : emptyTreeNotice
                    }
                  >
                    <Select
                      aria-label={ariaLabel}
                      options={options}
                      showSearch={{ optionFilterProp: 'label' }}
                      mode={isMulti ? 'multiple' : undefined}
                      loading={resolvedOptionsLoading}
                      status={resolvedOptionsError ? 'error' : undefined}
                      disabled={awaitingGate || emptyTreeNotice !== undefined}
                      placeholder={gatePlaceholder}
                    />
                  </Form.Item>
                );
              }

              if (param.type === 'date') {
                const dateFormat = param.format ?? DEFAULT_DATE_PARAMETER_FORMAT;
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                  >
                    <DatePicker
                      aria-label={ariaLabel}
                      style={{ width: '100%' }}
                      format={dateFormat}
                    />
                  </Form.Item>
                );
              }

              if (param.type === 'shippingTracker') {
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                  >
                    <ShippingTrackerCompactUI
                      aria-label={ariaLabel}
                      fieldId={param.parameterId}
                    />
                  </Form.Item>
                );
              }

              if (param.type === 'tableColumnSelect') {
                const options = resolvedOptionsByParamId[param.parameterId] ?? [];
                const hasConflict = param.scope === 'selectedRows' && options.length > 1;
                const currentValue = formValues?.[param.parameterId];
                const hasOverride = param.scope === 'selectedRows'
                  && !hasConflict
                  && options.length === 1
                  && typeof currentValue === 'string'
                  && currentValue !== ''
                  && currentValue !== options[0].value;
                const awaitingGate = !!param.filterBy && gatingValueByParamId[param.parameterId] == null;
                const gatePlaceholder = awaitingGate
                  ? `Select ${gatingParamLabelByParamId[param.parameterId] ?? param.filterBy!.parameterId} first`
                  : undefined;
                const warningHelp = hasConflict
                  ? `Selected rows contain multiple values for ${param.label} (${options.map((o) => o.value).join(', ')}). Pick one to apply to all selected rows.`
                  : hasOverride
                  ? `Submitting will overwrite ${param.label} in all selected rows (currently ${options[0].value}).`
                  : undefined;
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                    validateStatus={resolvedOptionsError ? 'error' : (hasConflict || hasOverride) ? 'warning' : undefined}
                    help={
                      resolvedOptionsError ? (
                        <FlexBox style={{ gap: 8, alignItems: 'center' }}>
                          <span>{resolvedOptionsError}</span>
                          <Button
                            type="link"
                            size="small"
                            aria-label={`retry-${param.parameterId}`}
                            onClick={handleRetryResolvedOptions}
                            disabled={isRunning}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            Retry
                          </Button>
                        </FlexBox>
                      ) : warningHelp
                    }
                  >
                    {param.allowFreeInput ? (
                      <AutoComplete
                        aria-label={ariaLabel}
                        options={options}
                        allowClear={!param.required}
                        status={resolvedOptionsError ? 'error' : undefined}
                        disabled={awaitingGate}
                        placeholder={gatePlaceholder}
                        showSearch={{
                          filterOption: (inputValue, option) =>
                            String(option?.label ?? '').toLowerCase().includes(inputValue.toLowerCase()),
                        }}
                      />
                    ) : (
                      <Select
                        aria-label={ariaLabel}
                        options={options}
                        showSearch={{ optionFilterProp: 'label' }}
                        loading={resolvedOptionsLoading}
                        allowClear={!param.required}
                        status={resolvedOptionsError ? 'error' : undefined}
                        disabled={awaitingGate}
                        placeholder={gatePlaceholder}
                      />
                    )}
                  </Form.Item>
                );
              }

              if (param.type === 'address') {
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                  >
                    <AddressUI aria-label={ariaLabel} />
                  </Form.Item>
                );
              }

              if (param.type === 'visibilitySelect') {
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                  >
                    <Select
                      aria-label={ariaLabel}
                      options={pdfVisibilitySelectOptions}
                      disabled={pdfVisibilitySelectOptions.length === 0}
                      placeholder="Default view"
                      variant="filled"
                    />
                  </Form.Item>
                );
              }

              if (param.type === 'distributorSelect') {
                return (
                  <Form.Item
                    key={param.parameterId}
                    name={fieldName(param)}
                    label={param.label}
                    rules={rules}
                  >
                    <DistributorSelectField
                      parameterId={param.parameterId}
                      workflowDocId={workflow.docId}
                      defaultValueSource={param.defaultValueSource}
                      entityReferences={sourceFormVersionEntityReferences}
                      onChange={(value) => {
                        form.setFieldValue(param.parameterId, value);
                      }}
                    />
                  </Form.Item>
                );
              }

              return (
                <Form.Item
                  key={param.parameterId}
                  name={fieldName(param)}
                  label={param.label}
                  rules={rules}
                >
                  <Select
                    aria-label={ariaLabel}
                    loading={usersSnap.loading}
                    options={userOptions}
                    showSearch={{ optionFilterProp: 'label' }}
                    allowClear={!param.required}
                  />
                </Form.Item>
              );
            })}
          </Form>
        )}

        {needsPdf && !includePdfOverride && (
          <>
            <Divider style={{ margin: 0 }} />
            <FlexBox style={{ paddingTop: token.padding }}>{previewControl}</FlexBox>
          </>
        )}

        {sendAction && (
          <>
            <Divider style={{ margin: 0 }} />
            <FlexCol stretch style={{ paddingTop: token.padding }}>
              {showRecipientPreview && (
                <FlexCol>
                  <Text strong>Recipients</Text>
                  {recipientPreviewLoading && (
                    <Text type="secondary">Loading recipients...</Text>
                  )}
                  {recipientPreviewError && (
                    <FlexBox style={{ gap: 8, alignItems: 'center' }}>
                      <Text type="danger">{recipientPreviewError}</Text>
                      <Button
                        type="link"
                        size="small"
                        aria-label="retry-recipient-preview"
                        onClick={handleRetryRecipientPreview}
                        disabled={isRunning}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        Retry
                      </Button>
                    </FlexBox>
                  )}
                  {!recipientPreviewLoading && !recipientPreviewError && recipientPreviewTargets.length === 0 && (
                    <Text type="secondary">Select targets to preview recipients.</Text>
                  )}
                  {recipientPreviewTargets.map((target) => {
                    const targetId = target.targetId;
                    const recipients = target.recipients ?? { to: [], cc: [], bcc: [] };
                    const overrides = recipientOverrides[targetId];
                    /**
                     * The send drops a target on its effective `to` list alone, exactly as the
                     * server does, so a Cc or Bcc address typed before a To address is work the
                     * run cannot honor. The controls stay on screen and disabled rather than
                     * disappearing, and the To control is never gated by this rule.
                     */
                    const effectiveTo = overrides?.to ?? recipients.to;
                    /**
                     * The sender's own Cc copy, listed where the send puts it: under Cc, unless
                     * the sender is already a To recipient of this target, and never under Bcc
                     * as well. It is steered by "Send me a copy", never by its row (SYD-2820).
                     */
                    const senderCopyEmail = senderCopyAddress && !effectiveTo.includes(senderCopyAddress)
                      ? senderCopyAddress
                      : undefined;
                    const targetListedEmails = new Set(
                      recipientRoleOrder.flatMap((r) => [...recipients[r], ...(overrides?.[r] ?? [])]),
                    );
                    return (
                      <FlexCol key={targetId} gap={5}>
                        <Text strong>{target.targetName ?? targetId}</Text>
                        {target.status !== 'ready' && (
                          <Text type="secondary">
                            {target.status === 'missingPOC' ? 'Missing POC' : 'Missing email'}
                          </Text>
                        )}
                        {recipientRoleOrder.map((role) => {
                          const selectedEmails = overrides?.[role] ?? recipients[role];
                          const roleSenderCopy = role === 'cc' ? senderCopyEmail : undefined;
                          const listedEmails = Array.from(new Set([
                            ...recipients[role],
                            ...(overrides?.[role] ?? []),
                            ...(roleSenderCopy ? [roleSenderCopy] : []),
                          ])).filter((email) => role !== 'bcc' || email !== senderCopyEmail);
                          const draftKey = `${targetId}-${role}`;
                          const draft = recipientDrafts[draftKey] ?? { value: '', blurred: false };
                          const normalizedDraft = normalizeRecipientEmail(draft.value);
                          const addDisabled = role !== 'to' && effectiveTo.length === 0;
                          /**
                           * Scoped to the target, not to the role: `resolveWorkflowRecipients`
                           * enforces `to > cc > bcc` disjointness, so an address offered for a
                           * second role of the same target is one the server would strip. The
                           * sender's Cc copy counts for Cc and Bcc only: adding the sender to To
                           * is honored, and the send then drops their copy.
                           */
                          const isDuplicate = normalizedDraft !== undefined
                            && (targetListedEmails.has(normalizedDraft) || (role !== 'to' && normalizedDraft === senderCopyEmail));
                          const addableEmail = isDuplicate ? undefined : normalizedDraft;
                          /**
                           * A duplicate reads as valid, so its reason shows the moment it is
                           * typed. A half-typed address is not an error yet — its message waits
                           * for the blur that commits it, and a further keystroke clears the
                           * flag so the field stops accusing the operator mid-word.
                           */
                          const draftError = isDuplicate
                            ? 'Already listed for this target'
                            : draft.blurred && draft.value.trim().length > 0 && normalizedDraft === undefined
                              ? 'Enter a valid email address'
                              : undefined;
                          return (
                            <FlexCol key={draftKey} gap={5}>
                              <Text type="secondary">{recipientRoleLabels[role]}</Text>
                              {listedEmails.length === 0 ? (
                                <Text type="secondary">No recipients</Text>
                              ) : (
                                <FlexCol gap={0}>
                                  {listedEmails.map((email) => (
                                    <Checkbox
                                      key={`${draftKey}-${email}`}
                                      aria-label={`recipient-toggle-${targetId}-${role}-${email}`}
                                      checked={email === roleSenderCopy || selectedEmails.includes(email)}
                                      disabled={isRunning || email === roleSenderCopy}
                                      onChange={(e) =>
                                        handleRecipientToggle(targetId, role, email, e.target.checked)
                                      }
                                    >
                                      {email}
                                    </Checkbox>
                                  ))}
                                </FlexCol>
                              )}
                              <Space.Compact style={{ width: '100%' }}>
                                <Input
                                  size="small"
                                  value={draft.value}
                                  disabled={addDisabled || isRunning}
                                  status={draftError ? 'error' : undefined}
                                  placeholder={`Add ${recipientRoleLabels[role]} address`}
                                  aria-label={`recipient-add-input-${targetId}-${role}`}
                                  onChange={(e) =>
                                    setRecipientDrafts((prev) => ({
                                      ...prev,
                                      [draftKey]: { value: e.target.value, blurred: false },
                                    }))
                                  }
                                  onBlur={() =>
                                    setRecipientDrafts((prev) => ({
                                      ...prev,
                                      [draftKey]: { value: prev[draftKey]?.value ?? '', blurred: true },
                                    }))
                                  }
                                  onPressEnter={() => {
                                    if (addableEmail) {
                                      handleRecipientAdd(targetId, role, addableEmail);
                                      return;
                                    }
                                    setRecipientDrafts((prev) => ({
                                      ...prev,
                                      [draftKey]: { value: prev[draftKey]?.value ?? '', blurred: true },
                                    }));
                                  }}
                                />
                                <Button
                                  size="small"
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  disabled={addDisabled || !addableEmail || isRunning}
                                  aria-label={`recipient-add-${targetId}-${role}`}
                                  onClick={() => {
                                    if (addableEmail) handleRecipientAdd(targetId, role, addableEmail);
                                  }}
                                >
                                  Add
                                </Button>
                              </Space.Compact>
                              {addDisabled && (
                                <Text type="secondary">Add a To address first</Text>
                              )}
                              {!addDisabled && draftError && <Text type="danger">{draftError}</Text>}
                            </FlexCol>
                          );
                        })}
                      </FlexCol>
                    );
                  })}
                </FlexCol>
              )}
              <FlexCol>
                <Text strong>Reply to</Text>
                {emailSenderPolicy?.allowUserChoice === false ? (
                  <Text>
                    {replyToOptions.find((option) => option.value === emailSenderMode)?.label}
                  </Text>
                ) : (
                  <Select
                    options={replyToOptions}
                    value={emailSenderMode}
                    onChange={(value) => setEmailSenderModeOverride(value)}
                    disabled={isRunning}
                    aria-label="workflow-email-sender-mode"
                    variant="filled"
                    style={{ width: '100%' }}
                  />
                )}
                <Checkbox
                  aria-label="workflow-send-me-a-copy"
                  checked={copySender}
                  disabled={isRunning || copySenderFixed}
                  onChange={(e) => setCopySenderOverride(e.target.checked)}
                >
                  Send me a copy
                </Checkbox>
                {emailSenderMode === 'user' && !user?.email && (
                  <Text type="warning">Your account has no email address configured</Text>
                )}
              </FlexCol>
              <FlexCol gap={5}>
                <Text strong>Export view</Text>
                <FlexBox style={{ alignItems: 'flex-end', gap: token.paddingXS }}>
                  <Select
                    options={pdfVisibilitySelectOptions}
                    value={pdfVisibilityMode}
                    onChange={(value) => setPdfVisibilityMode(value)}
                    placeholder="Generic PDF export view"
                    disabled={pdfVisibilitySelectOptions.length === 0 || isRunning}
                    aria-label="workflow-pdf-visibility"
                    variant="filled"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  {includePdfOverride && previewControl}
                </FlexBox>
              </FlexCol>
              {emptyTargetIds.size > 0 && emptyTargetIds.size < selectedTargetIds.length && (
                <Text type="warning">
                  No rows for {emptyTargetLabels.join(', ')} in their documents — they will be excluded from this run.
                </Text>
              )}
              {templateEntries.length >= 1 && (
                <FlexCol gap={5}>
                  <Text strong>Email template</Text>
                  <Select
                    options={templateSelectOptions}
                    value={selectedTemplateId}
                    onChange={(id) => {
                      const resolved = resolveForTemplate(id);
                      if (!resolved) return;
                      setSelectedTemplateId(id);
                      setSubjectOverride(resolved.subject || fallbackSubject);
                      setMessageOverride(resolved.message);
                      setSignatureOverride(resolved.signature);
                    }}
                    disabled={isRunning}
                    aria-label="workflow-email-template"
                    variant="filled"
                  />
                </FlexCol>
              )}
              <FlexCol gap={5}>
                <Text strong>Subject</Text>
                <Input
                  value={subjectOverride}
                  onChange={(e) => setSubjectOverride(e.target.value)}
                  disabled={isRunning}
                  aria-label="workflow-email-subject"
                  variant="filled"
                />
              </FlexCol>
              <FlexCol gap={5}>
                <Text strong>Message</Text>
                <TextArea
                  value={messageOverride}
                  onChange={(e) => setMessageOverride(e.target.value)}
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  placeholder="Optional message to include with the form"
                  disabled={isRunning}
                  aria-label="workflow-email-message"
                  maxLength={MAX_LENGTHS.longText}
                  showCount
                  variant="filled"
                />
              </FlexCol>
              <FlexCol gap={5}>
                <Text strong>Signature</Text>
                <TextArea
                  value={signatureOverride}
                  onChange={(e) => setSignatureOverride(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  placeholder="Optional signature"
                  disabled={isRunning}
                  aria-label="workflow-email-signature"
                  maxLength={MAX_LENGTHS.longText}
                  showCount
                  variant="filled"
                />
              </FlexCol>
              <FlexCol>
                <Text strong>Attachments</Text>
                <Checkbox
                  aria-label="workflow-attachments-include-pdf"
                  checked={includePdfOverride}
                  disabled={isRunning}
                  onChange={(e) => setIncludePdfOverride(e.target.checked)}
                >
                  Include PDF attachment
                </Checkbox>
                <Checkbox
                  aria-label="workflow-attachments-include-xlsx"
                  checked={includeXlsx}
                  disabled={tableOptions.length === 0 || isRunning}
                  onChange={(e) => setIncludeXlsxOverride(e.target.checked)}
                >
                  Include XLSX (table) attachment
                </Checkbox>
                {tableOptions.length === 0 && (
                  <Text type="secondary">No tables available for XLSX export.</Text>
                )}
              </FlexCol>
              <FileLinksSelector
                selectedFiles={selectedFileLinks}
                onSelectedFilesChange={setSelectedFileLinks}
                availableAttachments={formAttachments}
                disabled={isRunning || isLoadingParams}
              />
            </FlexCol>
          </>
        )}

        <Divider style={{ margin: 0 }} />
        {runStatusBanner}
        <FlexBox
          stretch
        >
          <Button
            aria-label="workflow-parameter-sidebar-cancel"
            onClick={onCancel}
            type="link"
            danger
          >
            Cancel
          </Button>
          <Tooltip title={stopUnavailableReason}>
            <Button
              aria-label="workflow-parameter-sidebar-stop"
              danger
              loading={stopPhase === 'stopping'}
              disabled={stopPhase !== 'stoppable'}
              onClick={() => {
                stopPressedRef.current = true;
                void onStop().catch((error: unknown) => {
                  /**
                   * The ask never reached the server, so nothing about this run was
                   * stopped and the outcome must not be qualified with a sentence
                   * saying the stop arrived late — that would report a request that
                   * was refused as one that was merely slow (SYD-2443).
                   */
                  stopPressedRef.current = false;
                  messageApi.error(formatStopRefusalMessage(error));
                });
              }}
            >
              {stopPhase === 'stopping' ? 'Stopping…' : 'Stop'}
            </Button>
          </Tooltip>
          <Button
            aria-label={runPhase === 'done'
              ? 'Done, close workflow sidebar'
              : `Run workflow ${workflow.description?.shortLabel || workflow.docId || ''}`}
            type="primary"
            loading={isRunning}
            disabled={runPhase === 'idle' && runGateReason !== undefined}
            onClick={handleRunClick}
          >
            {runPhase === 'done' ? 'Done' : 'Run'}
          </Button>
        </FlexBox>
      </FlexCol>
      {showPdfPreview && (
        <FormExportModal
          onCancel={() => setIsPdfPreviewOpen(false)}
          initialVisibilityMode={pdfRenderVisibility}
          splitQuery={previewSplitQuery}
          title={previewTarget ? `Export PDF — ${previewTarget.label}` : undefined}
        />
      )}
      {portalNode}
    </>
  );
};

export default WorkflowParameterContent;
